import React, { useRef, createElement } from 'react';
import { motion, useInView } from 'framer-motion';
import { DownloadIcon, FileTextIcon, FileSpreadsheetIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/9pYpmBqnTFtudjrgYsyKLh/METRO-BRANDBOOK_Mesa_de_trabajo_1_copia_11.svg';
/* ------------------------------------------------------------------ */
/*  Office download helpers                                            */
/* ------------------------------------------------------------------ */
const BASE_OFFICE_STYLES = `
  @page { size: A4; margin: 18mm; }
  body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #0E0E12; font-size: 10.5pt; line-height: 1.55; margin: 0; }
  .page { page-break-after: always; padding: 0; }
  .page:last-child { page-break-after: auto; }
  h1 { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 56pt; font-weight: 800; color: #0E0E12; margin: 0 0 12pt 0; letter-spacing: -0.03em; line-height: 0.95; }
  h2 { font-size: 22pt; font-weight: 800; margin: 28pt 0 10pt 0; color: #0E0E12; letter-spacing: -0.015em; }
  h3 { font-size: 12pt; font-weight: 700; margin: 18pt 0 6pt 0; color: #0E0E12; text-transform: uppercase; letter-spacing: 0.08em; }
  h4 { font-size: 10pt; font-weight: 700; margin: 12pt 0 4pt 0; color: #1A2BFF; text-transform: uppercase; letter-spacing: 0.1em; }
  p { margin: 6pt 0; }
  table { border-collapse: collapse; width: 100%; margin: 10pt 0; font-size: 10pt; }
  th { background: #0E0E12; color: #FFFFFF; padding: 9pt 8pt; text-align: left; font-weight: 700; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.08em; }
  td { padding: 9pt 8pt; border-bottom: 1px solid #DDDFE5; vertical-align: top; }
  .meta { color: #6B6E78; font-size: 9pt; }
  .brand { color: #1A2BFF; }
  .ink-bg { background: #0E0E12; color: #FFFFFF; }
  .blue-bg { background: #1A2BFF; color: #FFFFFF; }
  .surface-bg { background: #EDEEF1; }
  .total-row td { background: #0E0E12; color: #FFFFFF; font-weight: 700; font-size: 12pt; border-bottom: none; }
  .total-row .brand { color: #FFFFFF; }
  .right { text-align: right; }
  .small { font-size: 8.5pt; color: #6B6E78; }
  .divider { border-top: 1px solid #0E0E12; margin: 16pt 0; }
  .thick-divider { border-top: 4pt solid #0E0E12; margin: 16pt 0; }
  .label { font-size: 8pt; color: #6B6E78; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 700; margin-bottom: 4pt; }
  .placeholder { color: #1A2BFF; background: #EDEEF1; padding: 1pt 5pt; border-radius: 2pt; font-family: 'Courier New', monospace; font-size: 9pt; font-weight: 700; }
  ul { padding-left: 18pt; margin: 6pt 0; }
  li { margin: 5pt 0; }
  .callout { background: #1A2BFF; color: #FFFFFF; padding: 18pt 22pt; margin: 14pt 0; }
  .callout strong { display: block; font-size: 8pt; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 6pt; opacity: 0.8; }
  .callout-text { font-size: 14pt; font-weight: 500; line-height: 1.35; }
  .chapter-mark { display: inline-block; font-family: 'Courier New', monospace; font-size: 9pt; letter-spacing: 0.15em; color: #1A2BFF; font-weight: 700; margin-bottom: 8pt; }
  .big-num { font-size: 120pt; font-weight: 800; color: #1A2BFF; line-height: 0.9; letter-spacing: -0.05em; }
  .desc-cell { font-size: 8.5pt; color: #6B6E78; font-style: italic; margin-top: 3pt; }
  .legal-section { font-size: 9pt; line-height: 1.6; }
  .legal-section h3 { font-size: 10pt; margin-top: 14pt; }
  .legal-section p { margin: 4pt 0; }
  .cover { padding: 40mm 0; }
  .footer-bar { border-top: 1pt solid #0E0E12; padding-top: 8pt; margin-top: 24pt; font-size: 8pt; color: #6B6E78; display: flex; justify-content: space-between; }
`;
function downloadOfficeDoc(bodyHTML: string, filename: string, type: 'word' | 'excel') {
  const mime = type === 'word' ? 'application/msword' : 'application/vnd.ms-excel';
  const ext = type === 'word' ? 'doc' : 'xls';
  const ns = type === 'word' ? 'xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"' : 'xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"';
  const wrapped = '<!DOCTYPE html><html ' + ns + ' xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8" /><title>' + filename + '</title><style>{`' + BASE_OFFICE_STYLES + '`}</style></head><body>' + bodyHTML + '</body></html>';
  const blob = new Blob(['\ufeff' + wrapped], {
    type: mime + ';charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename + '.' + ext;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
/* ------------------------------------------------------------------ */
/*  Shared legal / footer blocks                                       */
/* ------------------------------------------------------------------ */
const INVOICE_LEGAL_PAGE = `
<div class="page legal-section">
  <div class="chapter-mark">ANEXO · CONDICIONES LEGALES</div>
  <h2>Términos y condiciones</h2>
  <p class="small">Factura emitida conforme al Real Decreto 1619/2012, de 30 de noviembre, por el que se regulan las obligaciones de facturación, y a la Ley 37/1992 del Impuesto sobre el Valor Añadido.</p>

  <h3>1 · Identificación fiscal</h3>
  <p>Emisor: Metro Agency S.L. — NIF / CIF B12345678 — Domicilio fiscal: Carrer de la Marina, 145, 08013 Barcelona, España. Inscrita en el Registro Mercantil de Barcelona.</p>

  <h3>2 · Plazos y forma de pago</h3>
  <p>El pago deberá efectuarse en un plazo máximo de 30 días naturales desde la fecha de expedición de la factura, mediante transferencia bancaria a la cuenta indicada. El impago dentro del plazo devengará automáticamente intereses de demora equivalentes al tipo legal del dinero incrementado en ocho puntos porcentuales (Ley 3/2004, de 29 de diciembre), sin necesidad de requerimiento previo.</p>

  <h3>3 · Impuesto sobre el Valor Añadido (IVA)</h3>
  <p>Las cuotas de IVA repercutidas se aplican al tipo general del 21% conforme al artículo 90 de la Ley 37/1992. En operaciones intracomunitarias entre sujetos pasivos identificados (artículo 25), no se repercutirá IVA y deberá hacerse constar la mención "Operación intracomunitaria exenta. Inversión del sujeto pasivo".</p>

  <h3>4 · Retención de IRPF</h3>
  <p>En servicios prestados por profesionales personas físicas, se aplicará la retención correspondiente del Impuesto sobre la Renta de las Personas Físicas (artículo 101 LIRPF) al tipo del 15%, o del 7% durante los dos primeros años de actividad. Esta retención se hace constar de forma separada en la factura cuando proceda.</p>

  <h3>5 · Reclamaciones</h3>
  <p>Cualquier reclamación o discrepancia relativa a esta factura deberá comunicarse por escrito a hello@metroagency.co en un plazo máximo de 15 días naturales desde su recepción. Transcurrido dicho plazo, la factura se considerará aceptada en todos sus términos.</p>

  <h3>6 · Protección de datos</h3>
  <p>Los datos personales facilitados serán tratados por Metro Agency S.L. con la finalidad de gestionar la relación comercial y cumplir las obligaciones contables y fiscales, conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley Orgánica 3/2018. El interesado puede ejercer sus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a privacy@metroagency.co.</p>

  <h3>7 · Conservación</h3>
  <p>Esta factura y la documentación contable asociada se conservarán durante el período legalmente establecido (artículo 30 del Código de Comercio y artículo 66 de la Ley General Tributaria).</p>

  <h3>8 · Jurisdicción</h3>
  <p>Las partes, con renuncia expresa a cualquier otro fuero que pudiera corresponderles, se someten a los Juzgados y Tribunales de Barcelona para la resolución de cualquier controversia derivada de la presente factura.</p>

  <div class="footer-bar">
    <span>Metro Agency S.L. · NIF B12345678 · Barcelona</span>
    <span>metroagency.co</span>
  </div>
</div>
`;
const BUDGET_LEGAL_PAGE = `
<div class="page legal-section">
  <div class="chapter-mark">ANEXO · CONDICIONES GENERALES DE CONTRATACIÓN</div>
  <h2>Términos del presupuesto</h2>

  <h3>1 · Validez de la oferta</h3>
  <p>El presente presupuesto tiene una validez de 30 días naturales desde la fecha de emisión. Transcurrido dicho plazo, Metro Agency S.L. se reserva el derecho a revisar los importes y condiciones aquí indicados.</p>

  <h3>2 · Aceptación y formalización</h3>
  <p>La aceptación del presupuesto requiere la firma del Cliente y el pago del depósito inicial (30% del importe total). La aceptación implica la conformidad expresa con la totalidad de los términos descritos, incluidas las presentes condiciones generales.</p>

  <h3>3 · Calendario de pagos</h3>
  <p>El importe se abonará en tres hitos: 30% a la firma del contrato, 40% a la aprobación del diseño, y 30% al lanzamiento y entrega final. Cada pago se facturará con plazo de 15 días naturales, conforme al artículo 4 de la Ley 3/2004.</p>

  <h3>4 · Alcance e imprevistos</h3>
  <p>El alcance descrito en este documento es definitivo. Cualquier cambio, ampliación o adición solicitada por el Cliente se considerará fuera de alcance y se presupuestará por separado mediante una orden de cambio firmada. La partida de contingencia (10%) cubre refinamientos razonables identificados durante la ejecución, no nuevas funcionalidades.</p>

  <h3>5 · Plazos de entrega</h3>
  <p>Los plazos estimados están condicionados a la entrega puntual por parte del Cliente de contenidos, accesos, aprobaciones y materiales necesarios. Cualquier retraso imputable al Cliente prorrogará el calendario en igual medida sin penalización para Metro Agency S.L.</p>

  <h3>6 · Propiedad intelectual</h3>
  <p>Los derechos de propiedad intelectual sobre los entregables finales se transferirán al Cliente en el momento del pago íntegro de la última factura. Metro Agency S.L. conserva el derecho a utilizar los trabajos realizados en su portfolio y materiales de comunicación, salvo pacto contrario por escrito.</p>

  <h3>7 · Confidencialidad</h3>
  <p>Ambas partes se comprometen a tratar como confidencial toda información sensible compartida durante la relación contractual y a no divulgarla a terceros sin autorización expresa.</p>

  <h3>8 · Resolución anticipada</h3>
  <p>En caso de resolución anticipada por causas imputables al Cliente, Metro Agency S.L. facturará el trabajo realizado hasta esa fecha más una indemnización equivalente al 20% del importe pendiente. El depósito inicial no será reembolsable.</p>

  <h3>9 · Limitación de responsabilidad</h3>
  <p>La responsabilidad de Metro Agency S.L. queda limitada al importe total efectivamente facturado por el proyecto. No se asumirá responsabilidad por daños indirectos, lucro cesante o pérdida de oportunidad.</p>

  <h3>10 · Protección de datos y jurisdicción</h3>
  <p>Tratamiento de datos conforme al RGPD (UE) 2016/679 y LOPDGDD 3/2018. Para cualquier controversia, las partes se someten a los Juzgados y Tribunales de Barcelona, con renuncia expresa a cualquier otro fuero.</p>

  <div class="footer-bar">
    <span>Metro Agency S.L. · NIF B12345678 · Barcelona</span>
    <span>metroagency.co</span>
  </div>
</div>
`;
/* ------------------------------------------------------------------ */
/*  Document content — Invoice (Spain-compliant, multi-page)           */
/* ------------------------------------------------------------------ */
const invoiceHTML = `
<div class="page" style="padding:0">
  <!-- Full-bleed brand header -->
  <div style="background:#1A2BFF;color:#FFFFFF;padding:22mm 18mm 16mm 18mm">
    <table style="width:100%;border-collapse:collapse;border:none;margin:0">
      <tr style="border:none">
        <td style="border:none;padding:0;width:55%;vertical-align:top">
          <div style="font-family:'Courier New',monospace;font-size:8pt;letter-spacing:0.22em;opacity:0.75">METRO AGENCY · BARCELONA · FACTURA v2026</div>
          <div style="height:18mm"></div>
          <div style="font-size:9pt;letter-spacing:0.18em;opacity:0.7;margin-bottom:4pt">DOCUMENT</div>
          <div style="font-size:72pt;font-weight:800;line-height:0.88;letter-spacing:-0.035em;color:#FFFFFF">Factura.</div>
          <div style="font-size:9pt;opacity:0.85;margin-top:6pt">Where ideas connect.</div>
        </td>
        <td style="border:none;padding:0;text-align:right;vertical-align:top">
          <div style="font-size:9pt;letter-spacing:0.18em;opacity:0.7;margin-bottom:4pt">N.º DE FACTURA · SERIE A</div>
          <div style="font-size:38pt;font-weight:800;line-height:0.9;letter-spacing:-0.02em"><span style="background:rgba(255,255,255,0.18);padding:2pt 10pt;border-radius:4pt">2026-001</span></div>
          <div style="height:14mm"></div>
          <div style="font-size:9pt;line-height:1.65;opacity:0.92;text-align:right">
            <strong>Expedición</strong> · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ DD/MM/AAAA ]</span><br>
            <strong>Operación</strong> · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ DD/MM/AAAA ]</span><br>
            <strong>Vencimiento</strong> · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ DD/MM/AAAA ]</span>
          </div>
        </td>
      </tr>
    </table>
    <div style="border-top:1pt solid rgba(255,255,255,0.4);margin-top:14pt;padding-top:8pt;font-size:8pt;opacity:0.85;display:flex;justify-content:space-between">
      <span>METRO AGENCY S.L. · NIF B12345678 · Carrer de la Marina, 145 · 08013 Barcelona</span>
      <span>hello@metroagency.co · metroagency.co</span>
    </div>
  </div>

  <!-- Body -->
  <div style="padding:14mm 18mm 18mm 18mm">

  <table style="border:none;margin:0">
    <tr style="border:none">
      <td style="border:none;padding:0;width:50%;vertical-align:top">
        <div class="label">Datos del destinatario</div>
        <div style="font-weight:700;font-size:13pt;margin-top:2pt"><span class="placeholder">[ Razón social del cliente ]</span></div>
        <div class="small" style="margin-top:4pt">
          NIF / CIF · <span class="placeholder">[ XXXXXXXXX ]</span><br>
          Domicilio fiscal · <span class="placeholder">[ Dirección completa ]</span><br>
          CP · <span class="placeholder">[ 00000 ]</span> <span class="placeholder">[ Ciudad ]</span><br>
          País · <span class="placeholder">[ País ]</span><br>
          Contacto · <span class="placeholder">[ Email / Teléfono ]</span>
        </div>
      </td>
      <td style="border:none;padding:0;text-align:right;vertical-align:top">
        <div class="label">Proyecto</div>
        <div style="font-weight:700;font-size:13pt;margin-top:2pt"><span class="placeholder">[ Nombre del proyecto ]</span></div>
        <div class="small" style="margin-top:4pt">
          Ref. · <span class="placeholder">[ Proyecto-ID ]</span><br>
          Responsable · <span class="placeholder">[ Account lead ]</span><br>
          Periodo facturado · <span class="placeholder">[ MM/AAAA ]</span>
        </div>
      </td>
    </tr>
  </table>

  <div style="margin-top:20pt;display:flex;align-items:baseline;gap:14pt">
    <span style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700">01 ·</span>
    <h2 style="margin:0;font-size:22pt;font-weight:800;letter-spacing:-0.015em">Detalle de operaciones</h2>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:48%">Concepto / Descripción</th>
        <th class="right" style="width:10%">Cantidad</th>
        <th class="right" style="width:14%">Precio unit.</th>
        <th class="right" style="width:10%">Dto.</th>
        <th class="right" style="width:18%">Base imponible</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>
          <strong>Estrategia y discovery</strong>
          <div class="desc-cell">Investigación, workshops de posicionamiento, definición de objetivos y KPIs. <span class="placeholder">[ describir el alcance específico de esta línea ]</span></div>
        </td>
        <td class="right">20 h</td>
        <td class="right">€90,00</td>
        <td class="right">—</td>
        <td class="right">€1.800,00</td>
      </tr>
      <tr>
        <td>
          <strong>Sistema de diseño e identidad visual</strong>
          <div class="desc-cell">Logo, paleta, sistema tipográfico, librería de componentes. <span class="placeholder">[ describir entregables concretos ]</span></div>
        </td>
        <td class="right">40 h</td>
        <td class="right">€90,00</td>
        <td class="right">—</td>
        <td class="right">€3.600,00</td>
      </tr>
      <tr>
        <td>
          <strong>Diseño y desarrollo web</strong>
          <div class="desc-cell">Páginas, componentes interactivos, integración con CMS, optimización. <span class="placeholder">[ describir páginas o integraciones ]</span></div>
        </td>
        <td class="right">60 h</td>
        <td class="right">€90,00</td>
        <td class="right">—</td>
        <td class="right">€5.400,00</td>
      </tr>
      <tr>
        <td>
          <strong>Producción y lanzamiento</strong>
          <div class="desc-cell">QA, despliegue a producción, handover, soporte post-lanzamiento 30 días. <span class="placeholder">[ describir condiciones de soporte ]</span></div>
        </td>
        <td class="right">20 h</td>
        <td class="right">€90,00</td>
        <td class="right">—</td>
        <td class="right">€1.800,00</td>
      </tr>
      <tr>
        <td>
          <strong><span class="placeholder">[ Concepto adicional ]</span></strong>
          <div class="desc-cell"><span class="placeholder">[ Descripción libre de la línea ]</span></div>
        </td>
        <td class="right"><span class="placeholder">[ 0 ]</span></td>
        <td class="right"><span class="placeholder">[ €0,00 ]</span></td>
        <td class="right">—</td>
        <td class="right"><span class="placeholder">[ €0,00 ]</span></td>
      </tr>
    </tbody>
  </table>

  <div style="margin-top:22pt;display:flex;align-items:baseline;gap:14pt">
    <span style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700">02 ·</span>
    <h2 style="margin:0;font-size:22pt;font-weight:800;letter-spacing:-0.015em">Resumen fiscal</h2>
  </div>

  <table style="margin-top:6pt">
    <tbody>
      <tr><td>Base imponible total</td><td class="right">€12.600,00</td></tr>
      <tr><td>IVA repercutido — Tipo general 21%</td><td class="right">€2.646,00</td></tr>
      <tr><td>Retención IRPF (si aplica · 15% / 7% nuevos autónomos)</td><td class="right small">— €0,00 —</td></tr>
    </tbody>
  </table>

  <!-- Big total bar, brand treatment -->
  <table style="margin-top:8pt;width:100%;border-collapse:collapse">
    <tr>
      <td style="background:#0E0E12;color:#FFFFFF;padding:18pt 22pt;border:none">
        <div style="font-size:9pt;letter-spacing:0.18em;opacity:0.7;margin-bottom:4pt">TOTAL A PAGAR</div>
        <div style="font-size:32pt;font-weight:800;letter-spacing:-0.02em;line-height:1">€15.246,00</div>
      </td>
      <td style="background:#1A2BFF;color:#FFFFFF;padding:18pt 22pt;border:none;text-align:right;width:42%">
        <div style="font-size:8pt;letter-spacing:0.18em;opacity:0.85;margin-bottom:4pt">FRA 2026-001 · CONCEPTO</div>
        <div style="font-size:13pt;font-weight:600;line-height:1.25"><span style="background:rgba(255,255,255,0.18);padding:2pt 6pt;border-radius:3pt">[ Cliente ]</span> · <span style="background:rgba(255,255,255,0.18);padding:2pt 6pt;border-radius:3pt">[ Proyecto ]</span></div>
      </td>
    </tr>
  </table>

  <div style="margin-top:22pt;display:flex;align-items:baseline;gap:14pt">
    <span style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700">03 ·</span>
    <h2 style="margin:0;font-size:22pt;font-weight:800;letter-spacing:-0.015em">Forma de pago</h2>
  </div>

  <table style="border:none;margin-top:6pt">
    <tr style="border:none">
      <td style="border:none;padding:0;width:55%;vertical-align:top">
        <div class="small" style="line-height:1.75">
          <strong>Método</strong> · Transferencia bancaria<br>
          <strong>Plazo</strong> · 30 días naturales desde fecha de expedición<br>
          <strong>Titular</strong> · Metro Agency S.L.<br>
          <strong>Banco</strong> · <span class="placeholder">[ Entidad bancaria ]</span><br>
          <strong>IBAN</strong> · ES00 0000 0000 0000 0000 0000<br>
          <strong>BIC / SWIFT</strong> · METRAGNCY
        </div>
      </td>
      <td style="border:none;padding:0 0 0 14pt;vertical-align:top">
        <div style="background:#EDEEF1;border-left:4pt solid #1A2BFF;padding:14pt 16pt">
          <div style="font-family:'Courier New',monospace;font-size:8pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700;margin-bottom:6pt">PAYMENT NOTE</div>
          <div style="font-size:11pt;font-weight:500;line-height:1.45">Pagos recibidos = trabajo que avanza. Las primeras 48h tras el pago son las más productivas de un proyecto. Nos vemos al otro lado.</div>
        </div>
      </td>
    </tr>
  </table>

  <div class="footer-bar">
    <span>Factura conforme al Real Decreto 1619/2012 y Ley 37/1992 (IVA)</span>
    <span>Página 1 de 2 · Continúa en condiciones legales →</span>
  </div>

  </div><!-- /body padding -->
</div>

${INVOICE_LEGAL_PAGE}
`;
/* ------------------------------------------------------------------ */
/*  Budget — Spain-aware, line descriptions, legal page                */
/* ------------------------------------------------------------------ */
const budgetHTML = `
<div class="page" style="padding:0">
  <!-- Full-bleed dark brand header -->
  <div style="background:#0E0E12;color:#FFFFFF;padding:22mm 18mm 16mm 18mm">
    <table style="width:100%;border-collapse:collapse;border:none;margin:0">
      <tr style="border:none">
        <td style="border:none;padding:0;width:60%;vertical-align:top">
          <div style="font-family:'Courier New',monospace;font-size:8pt;letter-spacing:0.22em;color:#1A2BFF">METRO AGENCY · BARCELONA · PRESUPUESTO v2026</div>
          <div style="height:18mm"></div>
          <div style="font-size:9pt;letter-spacing:0.18em;opacity:0.7;margin-bottom:4pt">DOCUMENT</div>
          <div style="font-size:72pt;font-weight:800;line-height:0.88;letter-spacing:-0.035em;color:#FFFFFF">Presu&shy;puesto.</div>
          <div style="font-size:9pt;opacity:0.85;margin-top:6pt">Where ideas connect.</div>
        </td>
        <td style="border:none;padding:0;text-align:right;vertical-align:top">
          <div style="font-size:9pt;letter-spacing:0.18em;opacity:0.7;margin-bottom:4pt">VERSIÓN</div>
          <div style="font-size:38pt;font-weight:800;line-height:0.9;letter-spacing:-0.02em"><span style="background:rgba(255,255,255,0.12);padding:2pt 10pt;border-radius:4pt">v1.0</span></div>
          <div style="height:14mm"></div>
          <div style="font-size:9pt;line-height:1.65;opacity:0.92;text-align:right">
            <strong>Fecha</strong> · <span style="background:rgba(255,255,255,0.12);padding:1pt 5pt;border-radius:2pt">[ DD/MM/AAAA ]</span><br>
            <strong>Validez</strong> · 30 días naturales<br>
            <strong>Ref.</strong> · <span style="background:rgba(255,255,255,0.12);padding:1pt 5pt;border-radius:2pt">[ Proyecto-ID ]</span>
          </div>
        </td>
      </tr>
    </table>
    <div style="border-top:1pt solid rgba(255,255,255,0.4);margin-top:14pt;padding-top:8pt;font-size:8pt;opacity:0.85;display:flex;justify-content:space-between">
      <span>METRO AGENCY S.L. · NIF B12345678 · Barcelona, ES</span>
      <span>hello@metroagency.co · metroagency.co</span>
    </div>
  </div>

  <!-- Body -->
  <div style="padding:14mm 18mm 18mm 18mm">

  <table style="border:none;margin:0">
    <tr style="border:none">
      <td style="border:none;padding:0;width:33%;vertical-align:top">
        <div class="label">Cliente</div>
        <div style="font-weight:700;font-size:12pt;margin-top:2pt"><span class="placeholder">[ Razón social ]</span></div>
        <div class="small" style="margin-top:4pt">NIF · <span class="placeholder">[ XXXXXXXXX ]</span><br><span class="placeholder">[ Dirección ]</span></div>
      </td>
      <td style="border:none;padding:0;width:34%;vertical-align:top">
        <div class="label">Proyecto</div>
        <div style="font-weight:700;font-size:12pt;margin-top:2pt"><span class="placeholder">[ Nombre ]</span></div>
        <div class="small" style="margin-top:4pt">Ref. <span class="placeholder">[ ID ]</span></div>
      </td>
      <td style="border:none;padding:0;text-align:right;vertical-align:top">
        <div class="label">Preparado por</div>
        <div style="font-weight:700;font-size:12pt;margin-top:2pt"><span class="placeholder">[ Account lead ]</span></div>
        <div class="small" style="margin-top:4pt"><span class="placeholder">[ email@metroagency.co ]</span></div>
      </td>
    </tr>
  </table>

  <!-- The connection pull-quote -->
  <div style="background:#1A2BFF;color:#FFFFFF;padding:18pt 22pt;margin:22pt 0 18pt 0">
    <div style="font-family:'Courier New',monospace;font-size:8pt;letter-spacing:0.18em;opacity:0.8;margin-bottom:6pt">THE CONNECTION WE'RE MAKING</div>
    <div style="font-size:16pt;font-weight:600;line-height:1.35;letter-spacing:-0.005em"><span class="placeholder" style="background:rgba(255,255,255,0.22);color:#FFFFFF">[ Una frase sobre el insight estratégico que justifica este presupuesto. ]</span></div>
  </div>

  <div style="display:flex;align-items:baseline;gap:14pt;margin-top:22pt">
    <span style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700">01 ·</span>
    <h2 style="margin:0;font-size:22pt;font-weight:800;letter-spacing:-0.015em">Alcance por fase</h2>
  </div>

  ${[{
  phase: '01 · Discovery & Strategy',
  blurb: 'Investigación, posicionamiento y definición del problema. Donde decidimos qué construir y por qué.',
  items: [['Entrevistas con stakeholders', 'Hasta 8 sesiones de 45 min con equipo directivo y clientes clave.', '€1.200,00'], ['Auditoría e investigación', 'Análisis competitivo, audit de marca actual, benchmark sectorial.', '€900,00'], ['Workshop de posicionamiento', 'Sesión de medio día en remoto o presencial en Barcelona.', '€1.500,00']],
  total: '€3.600,00'
}, {
  phase: '02 · Design',
  blurb: 'Sistema visual completo. Tokens, componentes, aplicaciones. La capa de marca que viaja con todo lo demás.',
  items: [['Sistema de identidad visual', 'Logo, paleta, tipografía, principios de uso, no-no\u2019s.', '€2.800,00'], ['Librería de componentes', 'Sistema modular en Figma + documentación de uso.', '€1.800,00'], ['Aplicaciones de marca', 'Plantillas para email, redes, papelería, presentaciones.', '€1.400,00']],
  total: '€6.000,00'
}, {
  phase: '03 · Build',
  blurb: 'Construcción técnica. Frontend, integración con CMS, testing. Producción real, no prototipos.',
  items: [['Desarrollo frontend', 'React + Tailwind. Responsive completo. Accesibilidad WCAG AA.', '€3.200,00'], ['Integración CMS', 'Conexión a CMS headless del cliente o setup nuevo.', '€1.400,00'], ['QA y testing', 'Testing cross-browser, performance, accesibilidad.', '€800,00']],
  total: '€5.400,00'
}, {
  phase: '04 · Launch & Handover',
  blurb: 'Lanzamiento, formación y soporte inicial. El handover real, no un email con un .zip.',
  items: [['Producción y despliegue', 'Migración a producción, configuración de dominios y DNS.', '€600,00'], ['Client guide y formación', 'Sesión de formación + documento entregable de uso.', '€800,00'], ['Soporte post-lanzamiento (30 días)', 'Bug fixes, tweaks menores, dudas. Sin retainer.', '€800,00']],
  total: '€2.200,00'
}].map(p => `
<h4>${p.phase}</h4>
<p class="small" style="margin:0 0 8pt 0;font-style:italic">${p.blurb}</p>
<table>
  <thead>
    <tr><th style="width:30%">Línea</th><th>Descripción</th><th class="right" style="width:18%">Importe</th></tr>
  </thead>
  <tbody>
    ${p.items.map(([line, desc, amt]) => `<tr><td><strong>${line}</strong></td><td class="small">${desc}</td><td class="right">${amt}</td></tr>`).join('')}
    <tr class="total-row"><td colspan="2">Subtotal ${p.phase.split('·')[0].trim()}</td><td class="right">${p.total}</td></tr>
  </tbody>
</table>
`).join('')}

  <div style="display:flex;align-items:baseline;gap:14pt;margin-top:22pt">
    <span style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700">02 ·</span>
    <h2 style="margin:0;font-size:22pt;font-weight:800;letter-spacing:-0.015em">Resumen de inversión</h2>
  </div>

  <table style="margin-top:6pt">
    <tbody>
      <tr><td>Subtotal fases 01–04</td><td class="right">€17.200,00</td></tr>
      <tr><td>Contingencia (10%) — refinamientos en ejecución</td><td class="right">€1.720,00</td></tr>
      <tr><td>Base imponible</td><td class="right">€18.920,00</td></tr>
      <tr><td>IVA — Tipo general 21%</td><td class="right">€3.973,20</td></tr>
    </tbody>
  </table>

  <!-- Big total bar -->
  <table style="margin-top:8pt;width:100%;border-collapse:collapse">
    <tr>
      <td style="background:#1A2BFF;color:#FFFFFF;padding:18pt 22pt;border:none">
        <div style="font-size:9pt;letter-spacing:0.18em;opacity:0.85;margin-bottom:4pt">TOTAL INVERSIÓN</div>
        <div style="font-size:32pt;font-weight:800;letter-spacing:-0.02em;line-height:1">€22.893,20</div>
      </td>
      <td style="background:#0E0E12;color:#FFFFFF;padding:18pt 22pt;border:none;text-align:right;width:42%">
        <div style="font-size:8pt;letter-spacing:0.18em;opacity:0.6;margin-bottom:4pt">PAGOS · 30% · 40% · 30%</div>
        <div style="font-size:13pt;font-weight:600;line-height:1.25">Firma · Diseño · Lanzamiento</div>
      </td>
    </tr>
  </table>

  <div style="display:flex;align-items:baseline;gap:14pt;margin-top:22pt">
    <span style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF;font-weight:700">03 ·</span>
    <h2 style="margin:0;font-size:22pt;font-weight:800;letter-spacing:-0.015em">Calendario de pagos</h2>
  </div>

  <table style="margin-top:6pt">
    <thead><tr><th>Hito</th><th>Trigger</th><th class="right">% total</th><th class="right">Importe</th></tr></thead>
    <tbody>
      <tr><td><strong>Depósito</strong></td><td class="small">Firma del contrato. Inicia trabajo de Discovery.</td><td class="right">30%</td><td class="right">€6.867,96</td></tr>
      <tr><td><strong>Mid-project</strong></td><td class="small">Aprobación del sistema de diseño.</td><td class="right">40%</td><td class="right">€9.157,28</td></tr>
      <tr><td><strong>Final</strong></td><td class="small">Lanzamiento y handover.</td><td class="right">30%</td><td class="right">€6.867,96</td></tr>
    </tbody>
  </table>

  <div class="footer-bar">
    <span>Presupuesto válido 30 días · Sujeto a las condiciones generales del anexo</span>
    <span>Página 1 de 2 · Continúa →</span>
  </div>

  </div><!-- /body padding -->
</div>

${BUDGET_LEGAL_PAGE}
`;
/* ------------------------------------------------------------------ */
/*  Project Dossier — bold, multi-page                                  */
/* ------------------------------------------------------------------ */
const dossierHTML = `
<!-- COVER -->
<div class="page cover" style="background:#1A2BFF;color:#FFFFFF;padding:30mm 18mm;min-height:240mm">
  <div style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;opacity:0.7">METRO AGENCY · BARCELONA · PROJECT DOSSIER</div>
  <div style="height:80mm"></div>
  <div style="font-size:11pt;letter-spacing:0.2em;opacity:0.8;margin-bottom:8pt">A PROJECT FOR</div>
  <div style="font-size:24pt;font-weight:700;margin-bottom:30pt"><span style="background:rgba(255,255,255,0.18);padding:2pt 10pt;border-radius:3pt">[ Client Name ]</span></div>
  <div style="font-size:72pt;font-weight:800;line-height:0.92;letter-spacing:-0.035em;color:#FFFFFF">
    <span style="background:rgba(255,255,255,0.18);padding:2pt 12pt;border-radius:4pt">[ Project ]</span>
  </div>
  <div style="height:50mm"></div>
  <div style="border-top:1pt solid rgba(255,255,255,0.4);padding-top:10pt;display:flex;justify-content:space-between;font-size:9pt;opacity:0.85">
    <span>Where ideas connect.</span>
    <span>v<span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">1.0</span> · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ Date ]</span></span>
  </div>
</div>

<!-- TOC -->
<div class="page">
  <div class="chapter-mark">CONTENTS</div>
  <h1 style="font-size:64pt">In&shy;side.</h1>
  <table style="margin-top:30pt;border:none">
    ${[['01', 'Executive summary', '02'], ['02', 'The opportunity', '03'], ['03', 'Scope & deliverables', '04'], ['04', 'Timeline', '05'], ['05', 'Team', '06'], ['06', 'Investment', '07'], ['07', 'Terms & conditions', '08']].map(([num, title, page]) => `<tr style="border:none"><td style="border:none;padding:8pt 0;width:60pt;font-family:'Courier New',monospace;color:#1A2BFF;font-weight:700;font-size:11pt">${num}</td><td style="border:none;padding:8pt 0;font-size:16pt;font-weight:600;letter-spacing:-0.01em">${title}</td><td style="border:none;padding:8pt 0;text-align:right;color:#6B6E78;font-family:'Courier New',monospace">p.${page}</td></tr>`).join('')}
  </table>
  <div class="footer-bar"><span>Metro Agency · Project dossier</span><span>p.01</span></div>
</div>

<!-- 01 EXEC SUMMARY -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">01</span>
    <div>
      <div class="chapter-mark">EXECUTIVE SUMMARY</div>
      <h2 style="margin:0">What we're building, in one page.</h2>
    </div>
  </div>
  <p style="font-size:13pt;line-height:1.5"><span class="placeholder">[ One paragraph describing what we're building, for whom, and why. Keep this to 4–6 sentences. The version that survives if the rest gets skimmed. ]</span></p>

  <div class="callout">
    <strong>The connection we're making</strong>
    <div class="callout-text"><span class="placeholder" style="background:rgba(255,255,255,0.22);color:#FFFFFF">[ One sentence on the strategic insight that drives the project. ]</span></div>
  </div>

  <h3>Three things to know</h3>
  <table style="border:none">
    ${[['01', 'What success looks like', '[ Define the outcome in measurable terms — traffic, signups, retention, NPS, whatever. ]'], ['02', 'What\u2019s different about our approach', '[ State the angle. Not features. The take. ]'], ['03', 'What we need from you', '[ Time, access, decisions. Be specific. ]']].map(([n, t, b]) => `<tr style="border:none"><td style="border:none;padding:10pt 0;width:50pt;font-family:'Courier New',monospace;color:#1A2BFF;font-weight:700;vertical-align:top">${n}</td><td style="border:none;padding:10pt 0"><div style="font-weight:700;font-size:11pt;margin-bottom:3pt">${t}</div><div class="small"><span class="placeholder">${b}</span></div></td></tr>`).join('')}
  </table>
  <div class="footer-bar"><span>01 · Executive summary</span><span>p.02</span></div>
</div>

<!-- 02 OPPORTUNITY -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">02</span>
    <div>
      <div class="chapter-mark">THE OPPORTUNITY</div>
      <h2 style="margin:0">Why now. Why this. Why us.</h2>
    </div>
  </div>

  <h3>The context</h3>
  <p><span class="placeholder">[ Market, audience, competitive landscape. Where the client sits today and what's shifting. 2–3 paragraphs. ]</span></p>

  <h3>The insight</h3>
  <div class="ink-bg" style="padding:30pt 28pt;margin:14pt 0">
    <div style="font-size:9pt;letter-spacing:0.15em;opacity:0.7;margin-bottom:10pt">THE INSIGHT</div>
    <div style="font-size:24pt;font-weight:700;line-height:1.2;letter-spacing:-0.015em"><span class="placeholder" style="background:rgba(255,255,255,0.18);color:#FFFFFF">[ The one observation that makes this project make sense. ]</span></div>
  </div>

  <h3>The approach</h3>
  <p><span class="placeholder">[ How we're going to act on the insight. Strategic angle in 3–5 sentences. ]</span></p>

  <div class="footer-bar"><span>02 · The opportunity</span><span>p.03</span></div>
</div>

<!-- 03 SCOPE -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">03</span>
    <div>
      <div class="chapter-mark">SCOPE & DELIVERABLES</div>
      <h2 style="margin:0">What you'll have when we're done.</h2>
    </div>
  </div>

  <table>
    <thead><tr><th style="width:35%">Workstream</th><th>Deliverables</th><th class="right" style="width:14%">Format</th></tr></thead>
    <tbody>
      <tr><td><strong>Strategy</strong><div class="desc-cell">Discovery, positioning</div></td><td><span class="placeholder">[ Positioning doc, brand strategy summary, key messages ]</span></td><td class="right small">PDF + .doc</td></tr>
      <tr><td><strong>Identity</strong><div class="desc-cell">Logo, system, tokens</div></td><td><span class="placeholder">[ Logo set, color tokens, type system, component library ]</span></td><td class="right small">SVG + Figma</td></tr>
      <tr><td><strong>Web</strong><div class="desc-cell">Site build</div></td><td><span class="placeholder">[ Pages, CMS, deployment ]</span></td><td class="right small">Live URL</td></tr>
      <tr><td><strong>Production</strong><div class="desc-cell">Applications</div></td><td><span class="placeholder">[ Email templates, social kit, presentation deck ]</span></td><td class="right small">Editable</td></tr>
    </tbody>
  </table>

  <h3>Out of scope</h3>
  <ul>
    <li><span class="placeholder">[ Anything explicitly excluded — paid media, ongoing content, etc. ]</span></li>
  </ul>

  <div class="footer-bar"><span>03 · Scope & deliverables</span><span>p.04</span></div>
</div>

<!-- 04 TIMELINE -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">04</span>
    <div>
      <div class="chapter-mark">TIMELINE</div>
      <h2 style="margin:0">Nine weeks, four stations.</h2>
    </div>
  </div>

  <table>
    <thead><tr><th style="width:24%">Phase</th><th style="width:18%">Weeks</th><th>Key milestone</th><th class="right" style="width:18%">Status</th></tr></thead>
    <tbody>
      <tr><td><strong>Discovery & Strategy</strong></td><td>1–2</td><td>Positioning approved</td><td class="right brand">●●●●○</td></tr>
      <tr><td><strong>Design</strong></td><td>3–5</td><td>Visual system signed off</td><td class="right brand">●●○○○</td></tr>
      <tr><td><strong>Build</strong></td><td>6–8</td><td>Staging review</td><td class="right brand">○○○○○</td></tr>
      <tr><td><strong>Launch</strong></td><td>9</td><td>Live · client trained</td><td class="right brand">○○○○○</td></tr>
    </tbody>
  </table>

  <h3>Dependencies on you</h3>
  <ul>
    <li>Stakeholder interviews scheduled in the first 5 working days.</li>
    <li>Design feedback consolidated into one voice per review round.</li>
    <li>Content (copy, photos, assets) delivered by end of week 5.</li>
    <li>Final approvals from a single decision-maker — not a committee.</li>
  </ul>

  <div class="footer-bar"><span>04 · Timeline</span><span>p.05</span></div>
</div>

<!-- 05 TEAM -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">05</span>
    <div>
      <div class="chapter-mark">TEAM</div>
      <h2 style="margin:0">The people on this train.</h2>
    </div>
  </div>

  <table>
    <thead><tr><th>Role</th><th>Name</th><th>Focus</th><th class="right">Time on project</th></tr></thead>
    <tbody>
      <tr><td><strong>Account & strategy lead</strong></td><td><span class="placeholder">[ Name ]</span></td><td class="small">Owns the relationship and the work. Your single point of contact.</td><td class="right">Full project</td></tr>
      <tr><td><strong>Creative director</strong></td><td><span class="placeholder">[ Name ]</span></td><td class="small">Sets the visual direction. Reviews every output.</td><td class="right">Discovery + Design</td></tr>
      <tr><td><strong>Designer</strong></td><td><span class="placeholder">[ Name ]</span></td><td class="small">Builds the system, applies it across surfaces.</td><td class="right">Design + Build</td></tr>
      <tr><td><strong>Developer</strong></td><td><span class="placeholder">[ Name ]</span></td><td class="small">Frontend, CMS, deployment, performance.</td><td class="right">Build + Launch</td></tr>
    </tbody>
  </table>

  <div class="callout">
    <strong>Single point of contact</strong>
    <div class="callout-text">Everything flows through the account lead. You won't be chasing four people for one decision.</div>
  </div>

  <div class="footer-bar"><span>05 · Team</span><span>p.06</span></div>
</div>

<!-- 06 INVESTMENT -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">06</span>
    <div>
      <div class="chapter-mark">INVESTMENT</div>
      <h2 style="margin:0">What it costs.</h2>
    </div>
  </div>

  <table>
    <thead><tr><th>Phase</th><th class="right">Subtotal</th></tr></thead>
    <tbody>
      <tr><td><strong>01 · Discovery & Strategy</strong><div class="desc-cell">Interviews, audit, positioning workshop</div></td><td class="right">€3.600,00</td></tr>
      <tr><td><strong>02 · Design</strong><div class="desc-cell">Identity, components, applications</div></td><td class="right">€6.000,00</td></tr>
      <tr><td><strong>03 · Build</strong><div class="desc-cell">Frontend, CMS, QA</div></td><td class="right">€5.400,00</td></tr>
      <tr><td><strong>04 · Launch & Handover</strong><div class="desc-cell">Production, training, 30-day support</div></td><td class="right">€2.200,00</td></tr>
    </tbody>
  </table>

  <table style="margin-top:14pt">
    <tbody>
      <tr><td>Subtotal</td><td class="right">€17.200,00</td></tr>
      <tr><td>Contingency (10%)</td><td class="right">€1.720,00</td></tr>
      <tr><td>VAT 21%</td><td class="right">€3.973,20</td></tr>
      <tr class="total-row"><td>TOTAL INVESTMENT</td><td class="right">€22.893,20</td></tr>
    </tbody>
  </table>

  <p class="small">Phase-level breakdown by line item lives in the accompanying budget document (.xls).</p>

  <h3>Payment schedule</h3>
  <table>
    <thead><tr><th>Stage</th><th>Trigger</th><th class="right">% of total</th></tr></thead>
    <tbody>
      <tr><td>Deposit</td><td>Contract signature</td><td class="right">30%</td></tr>
      <tr><td>Mid-project</td><td>Design approval</td><td class="right">40%</td></tr>
      <tr><td>Final</td><td>Launch & handover</td><td class="right">30%</td></tr>
    </tbody>
  </table>

  <div class="footer-bar"><span>06 · Investment</span><span>p.07</span></div>
</div>

<!-- 07 TERMS -->
<div class="page legal-section">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">07</span>
    <div>
      <div class="chapter-mark">TERMS & CONDITIONS</div>
      <h2 style="margin:0">The fine print, made plain.</h2>
    </div>
  </div>

  <h3>1 · Validity</h3>
  <p>This dossier and its commercial terms are valid for 30 calendar days from the date on the cover. Beyond that, we reserve the right to revisit pricing and timeline.</p>

  <h3>2 · Engagement</h3>
  <p>Project starts on contract signature and payment of the deposit (30%). Discovery begins within 5 working days of receipt of funds.</p>

  <h3>3 · Scope</h3>
  <p>The scope described in section 03 is fixed. Additions and changes are handled through signed change orders. The 10% contingency covers refinements identified during execution — not new features.</p>

  <h3>4 · Intellectual property</h3>
  <p>All final deliverables transfer to the client upon full payment of the final invoice. We retain the right to feature the work in our portfolio unless agreed otherwise in writing.</p>

  <h3>5 · Payments & late fees</h3>
  <p>Invoices are issued at each milestone with 15-day net terms. Late payments accrue legal interest plus 8 percentage points (Law 3/2004), automatically and without prior notice.</p>

  <h3>6 · Confidentiality</h3>
  <p>Both parties agree to keep confidential any sensitive information shared during the engagement.</p>

  <h3>7 · Cancellation</h3>
  <p>If cancelled by the client, work delivered to date is billed plus 20% of the outstanding amount as compensation. Deposit is non-refundable.</p>

  <h3>8 · Liability</h3>
  <p>Liability is capped at the total amount actually invoiced. We are not liable for indirect or consequential damages.</p>

  <h3>9 · Data & jurisdiction</h3>
  <p>Personal data handled under GDPR (EU) 2016/679 and LOPDGDD 3/2018. Any dispute is submitted to the courts of Barcelona, with express waiver of any other jurisdiction.</p>

  <div class="footer-bar"><span>07 · Terms & conditions</span><span>p.08</span></div>
</div>

<!-- SIGN-OFF -->
<div class="page" style="background:#0E0E12;color:#FFFFFF;padding:40mm 18mm;min-height:240mm">
  <div class="chapter-mark" style="color:#1A2BFF">SIGN-OFF</div>
  <div style="font-size:48pt;font-weight:800;letter-spacing:-0.025em;line-height:0.95;margin:18pt 0 30pt">Let's see<br>where this<br>goes.</div>

  <table style="border:none;margin-top:40pt">
    <tr style="border:none">
      <td style="border:none;padding:0;width:50%;vertical-align:top">
        <div style="font-size:8pt;letter-spacing:0.15em;opacity:0.6;margin-bottom:6pt">FOR THE CLIENT</div>
        <div style="border-bottom:1pt solid rgba(255,255,255,0.4);height:50pt"></div>
        <div class="small" style="margin-top:6pt;opacity:0.8">Name · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ Name ]</span> · Date · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ Date ]</span></div>
      </td>
      <td style="border:none;padding:0;width:8%"></td>
      <td style="border:none;padding:0;width:42%;vertical-align:top">
        <div style="font-size:8pt;letter-spacing:0.15em;opacity:0.6;margin-bottom:6pt">FOR METRO AGENCY</div>
        <div style="border-bottom:1pt solid rgba(255,255,255,0.4);height:50pt"></div>
        <div class="small" style="margin-top:6pt;opacity:0.8">Name · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ Account lead ]</span> · Date · <span style="background:rgba(255,255,255,0.18);padding:1pt 5pt;border-radius:2pt">[ Date ]</span></div>
      </td>
    </tr>
  </table>

  <div style="position:relative;margin-top:60pt;border-top:1pt solid rgba(255,255,255,0.3);padding-top:10pt;font-size:8pt;opacity:0.7;display:flex;justify-content:space-between">
    <span>Metro Agency S.L. · NIF B12345678 · Carrer de la Marina, 145 · 08013 Barcelona</span>
    <span>metroagency.co</span>
  </div>
</div>
`;
/* ------------------------------------------------------------------ */
/*  Client Guide — bold, multi-page                                     */
/* ------------------------------------------------------------------ */
const clientGuideHTML = `
<!-- COVER -->
<div class="page cover" style="background:#0E0E12;color:#FFFFFF;padding:30mm 18mm;min-height:240mm;position:relative">
  <div style="font-family:'Courier New',monospace;font-size:9pt;letter-spacing:0.2em;color:#1A2BFF">METRO AGENCY · PROJECT HANDOVER · v2026</div>
  <div style="height:60mm"></div>
  <div style="font-size:11pt;letter-spacing:0.2em;opacity:0.6;margin-bottom:8pt">A GUIDE FOR</div>
  <div style="font-size:20pt;font-weight:700;margin-bottom:30pt"><span style="background:rgba(255,255,255,0.12);padding:2pt 10pt;border-radius:3pt">[ Client ]</span></div>
  <h1 style="color:#FFFFFF;font-size:90pt;line-height:0.9;margin:0">Your<br>project,<br><span style="color:#1A2BFF">now live.</span></h1>
  <div style="height:30mm"></div>
  <div style="font-size:14pt;opacity:0.85;max-width:120mm">A handover guide to everything we built together — where it lives, how to use it, and when to ring us up.</div>
  <div style="position:absolute;bottom:30mm;left:18mm;right:18mm;border-top:1pt solid rgba(255,255,255,0.4);padding-top:10pt;font-size:9pt;opacity:0.8;display:flex;justify-content:space-between">
    <span>For <span style="background:rgba(255,255,255,0.12);padding:1pt 5pt;border-radius:2pt">[ Project ]</span></span>
    <span>Handed over <span style="background:rgba(255,255,255,0.12);padding:1pt 5pt;border-radius:2pt">[ Date ]</span></span>
  </div>
</div>

<!-- TOC + WELCOME -->
<div class="page">
  <div class="chapter-mark">WELCOME</div>
  <h1 style="font-size:48pt">Read this once.<br>Keep it close.</h1>
  <p style="font-size:13pt;margin-top:18pt">This document is the handover for everything we built together. It covers where your assets live, how to use them, what you can change yourself, and when to call us. It is short on purpose. Skim it now, bookmark it, come back when you need something specific.</p>

  <h3>Sections</h3>
  <table style="border:none">
    ${[['01', 'Brand assets', 'Where they live · how to use'], ['02', 'Website', 'Admin access · what you can change'], ['03', 'Content & voice', 'How to write in the brand'], ['04', 'Support', 'When to DIY · when to call us'], ['05', 'Contact', 'The humans behind the work']].map(([n, t, d]) => `<tr style="border:none"><td style="border:none;padding:8pt 0;width:50pt;font-family:'Courier New',monospace;color:#1A2BFF;font-weight:700">${n}</td><td style="border:none;padding:8pt 0;font-size:14pt;font-weight:600">${t}</td><td style="border:none;padding:8pt 0;text-align:right;color:#6B6E78" class="small">${d}</td></tr>`).join('')}
  </table>
  <div class="footer-bar"><span>Client guide</span><span>p.01</span></div>
</div>

<!-- 01 BRAND ASSETS -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">01</span>
    <div>
      <div class="chapter-mark">BRAND ASSETS</div>
      <h2 style="margin:0">Where it lives. How to use it.</h2>
    </div>
  </div>

  <div class="callout">
    <strong>Your library</strong>
    <div class="callout-text"><span class="placeholder" style="background:rgba(255,255,255,0.22);color:#FFFFFF">[ Drive / DAM URL ]</span></div>
  </div>

  <h3>What's in there</h3>
  <table>
    <thead><tr><th>Folder</th><th>Contents</th><th class="right">Use for</th></tr></thead>
    <tbody>
      <tr><td><strong>/logo</strong></td><td>Master SVG, all colorways, mono versions, clear-space guide.</td><td class="right small">Anything with a logo on it</td></tr>
      <tr><td><strong>/fonts</strong></td><td>Anton, Space Grotesk, Helvetica Neue (system).</td><td class="right small">Documents, web, presentations</td></tr>
      <tr><td><strong>/colors</strong></td><td>Tokens · CSS, JSON, ASE for Adobe, .clr for macOS.</td><td class="right small">Designers & developers</td></tr>
      <tr><td><strong>/templates</strong></td><td>Email, social, presentation, document templates.</td><td class="right small">Everyday comms</td></tr>
      <tr><td><strong>/source</strong></td><td>Figma files, native source for everything.</td><td class="right small">Edits & extensions</td></tr>
    </tbody>
  </table>

  <h3>The non-negotiables</h3>
  <ul>
    <li><strong>Logo</strong> · Always use the master SVG. Never redraw, recolor outside the palette, or stretch.</li>
    <li><strong>Fonts</strong> · Anton on the logo only. Space Grotesk for headlines. Helvetica Neue for body.</li>
    <li><strong>Color</strong> · Metro Blue (#1A2BFF) is the primary. Secondary palette for accents — sparingly.</li>
  </ul>

  <div style="background:#1A2BFF;color:#FFFFFF;padding:18pt 22pt;margin-top:14pt">
    <strong style="display:block;font-size:8pt;letter-spacing:0.15em;text-transform:uppercase;margin-bottom:6pt;opacity:0.8">DON'T</strong>
    <div style="font-size:13pt;font-weight:500;line-height:1.4">Apply shadows, gradients, or glow to the logo. Stretch the wordmark. Use off-palette colors. Recreate the logo in another tool.</div>
  </div>

  <div class="footer-bar"><span>01 · Brand assets</span><span>p.02</span></div>
</div>

<!-- 02 WEBSITE -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">02</span>
    <div>
      <div class="chapter-mark">WEBSITE</div>
      <h2 style="margin:0">Driving the thing.</h2>
    </div>
  </div>

  <h3>Admin access</h3>
  <table style="border:none">
    <tr style="border:none"><td style="border:none;padding:6pt 0;width:40%" class="label">CMS URL</td><td style="border:none;padding:6pt 0"><span class="placeholder">[ CMS URL ]</span></td></tr>
    <tr style="border:none"><td style="border:none;padding:6pt 0" class="label">Admin email</td><td style="border:none;padding:6pt 0"><span class="placeholder">[ admin@client.com ]</span></td></tr>
    <tr style="border:none"><td style="border:none;padding:6pt 0" class="label">Hosting</td><td style="border:none;padding:6pt 0"><span class="placeholder">[ Provider · plan ]</span></td></tr>
    <tr style="border:none"><td style="border:none;padding:6pt 0" class="label">Domain</td><td style="border:none;padding:6pt 0"><span class="placeholder">[ Registrar · renewal date ]</span></td></tr>
  </table>

  <table style="margin-top:20pt">
    <thead><tr><th style="width:50%">What you can change yourself</th><th>What we change for you</th></tr></thead>
    <tbody>
      <tr>
        <td class="small">
          ✓ Page copy & images<br>
          ✓ Blog posts and case studies<br>
          ✓ Team members<br>
          ✓ Contact details & footer info<br>
          ✓ SEO meta on existing pages
        </td>
        <td class="small">
          ✕ Layout or template changes<br>
          ✕ New page types<br>
          ✕ Integrations (analytics, forms, payments)<br>
          ✕ Performance & accessibility audits<br>
          ✕ Schema markup edits
        </td>
      </tr>
    </tbody>
  </table>

  <h3>Common tasks · 60-second cheatsheet</h3>
  <ul>
    <li><strong>Add a blog post</strong> · CMS → Posts → New → choose template → publish.</li>
    <li><strong>Swap a hero image</strong> · Pages → Home → Hero block → replace image (recommended 2400×1600, &lt;500kb).</li>
    <li><strong>Add a team member</strong> · CMS → Team → New → name, role, photo, short bio (under 80 words).</li>
    <li><strong>Update footer</strong> · CMS → Globals → Footer.</li>
  </ul>

  <div class="footer-bar"><span>02 · Website</span><span>p.03</span></div>
</div>

<!-- 03 CONTENT & VOICE -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">03</span>
    <div>
      <div class="chapter-mark">CONTENT & VOICE</div>
      <h2 style="margin:0">Writing in the brand.</h2>
    </div>
  </div>

  <p>Your voice is now part of the brand system. The full voice document is in your asset library. This is the pocket version.</p>

  <h3>The three rules</h3>
  <table>
    <tbody>
      <tr><td style="width:40%"><strong>Periods over exclamations</strong></td><td class="small">If the line is good, it doesn't need extra energy. If it isn't, the exclamation won't save it.</td></tr>
      <tr><td><strong>Connection over magic</strong></td><td class="small">Build language, not sales language. We make things connect — we don't perform tricks.</td></tr>
      <tr><td><strong>Direct, calm, real</strong></td><td class="small">Adult conversation. Confident, not enthusiastic.</td></tr>
    </tbody>
  </table>

  <h3>Words to use · words to avoid</h3>
  <table>
    <thead><tr><th style="width:50%;background:#1A2BFF">Use</th><th style="background:#0E0E12">Avoid</th></tr></thead>
    <tbody>
      <tr>
        <td class="small">Connect · network · line · route · station · build · craft · campaign · strategy · real · honest · clear · intentional</td>
        <td class="small">Magic · wow factor · rock · unleash · game-changer · cutting-edge · synergy · guru · ninja · disrupt · journey</td>
      </tr>
    </tbody>
  </table>

  <h3>Before & after</h3>
  <table>
    <tbody>
      <tr><td class="small" style="text-decoration:line-through;color:#6B6E78">Let's create some magic together!</td><td><strong>Let's create something that matters.</strong></td></tr>
      <tr><td class="small" style="text-decoration:line-through;color:#6B6E78">Ready to rock the visual game?!</td><td><strong>Ready to build something real?</strong></td></tr>
      <tr><td class="small" style="text-decoration:line-through;color:#6B6E78">Don't wait to add some WOW to your brand!</td><td><strong>Your brand deserves strategy that works.</strong></td></tr>
    </tbody>
  </table>

  <div class="footer-bar"><span>03 · Content & voice</span><span>p.04</span></div>
</div>

<!-- 04 SUPPORT -->
<div class="page">
  <div style="display:flex;align-items:baseline;gap:20pt;margin-bottom:14pt">
    <span class="big-num">04</span>
    <div>
      <div class="chapter-mark">SUPPORT</div>
      <h2 style="margin:0">When to call. When to DIY.</h2>
    </div>
  </div>

  <div class="callout">
    <strong>Your support window</strong>
    <div class="callout-text">30 days post-launch · bug fixes, small tweaks, and questions included. Then ongoing on retainer.</div>
  </div>

  <table>
    <thead><tr><th style="width:50%">Call us</th><th>Do it yourself</th></tr></thead>
    <tbody>
      <tr>
        <td class="small">
          → Something on the site is broken<br>
          → You need a new section, page, or template<br>
          → You want to discuss a new project<br>
          → Integration with a new tool<br>
          → Performance or accessibility regression
        </td>
        <td class="small">
          → Updating copy or swapping images<br>
          → Publishing new blog posts or case studies<br>
          → Updating team or contact info<br>
          → Routine CMS edits
        </td>
      </tr>
    </tbody>
  </table>

  <h3>Response times</h3>
  <table>
    <thead><tr><th>Severity</th><th>Description</th><th class="right">First response</th></tr></thead>
    <tbody>
      <tr><td><strong>Critical</strong></td><td class="small">Site down, checkout broken, data leak.</td><td class="right">Same business day</td></tr>
      <tr><td><strong>High</strong></td><td class="small">Feature broken, content not publishing.</td><td class="right">Within 1 business day</td></tr>
      <tr><td><strong>Normal</strong></td><td class="small">Cosmetic, tweaks, questions.</td><td class="right">Within 3 business days</td></tr>
    </tbody>
  </table>

  <div class="footer-bar"><span>04 · Support</span><span>p.05</span></div>
</div>

<!-- 05 CONTACT -->
<div class="page" style="background:#EDEEF1;padding:30mm 18mm;min-height:240mm">
  <div class="chapter-mark">CONTACT</div>
  <h1 style="font-size:64pt">The humans<br>behind it.</h1>

  <table style="border:none;margin-top:30pt">
    <tr style="border:none">
      <td style="border:none;padding:0;width:50%;vertical-align:top">
        <div class="label">Account lead</div>
        <div style="font-size:18pt;font-weight:700;margin:4pt 0"><span class="placeholder">[ Name ]</span></div>
        <div class="small"><span class="placeholder">[ email@metroagency.co ]</span></div>
      </td>
      <td style="border:none;padding:0;vertical-align:top">
        <div class="label">General</div>
        <div style="font-size:18pt;font-weight:700;margin:4pt 0">hello@metroagency.co</div>
        <div class="small">For new briefs, questions, anything.</div>
      </td>
    </tr>
    <tr style="border:none">
      <td style="border:none;padding:30pt 0 0 0;vertical-align:top">
        <div class="label">Studio</div>
        <div style="font-weight:600">Carrer de la Marina, 145<br>08013 Barcelona, España</div>
      </td>
      <td style="border:none;padding:30pt 0 0 0;vertical-align:top">
        <div class="label">Web</div>
        <div style="font-weight:600">metroagency.co</div>
      </td>
    </tr>
  </table>

  <div style="position:relative;margin-top:60pt;padding-top:14pt;border-top:1pt solid #0E0E12">
    <div style="font-size:42pt;font-weight:800;letter-spacing:-0.025em;line-height:1">Where ideas connect.</div>
    <div class="small" style="margin-top:8pt">Metro Agency S.L. · NIF B12345678</div>
  </div>
</div>
`;
/* ------------------------------------------------------------------ */
/*  UI components                                                      */
/* ------------------------------------------------------------------ */
function MiniPage({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={'rounded-sm border border-[var(--divider)] overflow-hidden shadow-sm bg-white ' + className} style={{
    aspectRatio: '210/297'
  }} data-id="element-901">
      {children}
    </div>;
}
function MultiPagePreview({
  label,
  format,
  pages
}: {
  label: string;
  format: string;
  pages: React.ReactNode[];
}) {
  return <div className="space-y-3" data-id="element-902">
      <div className="grid grid-cols-3 gap-2" data-id="element-903">
        {pages.map((p, i) => <MiniPage key={i} data-id="element-904">{p}</MiniPage>)}
      </div>
      <div className="flex items-center justify-between" data-id="element-905">
        <div className="font-space text-sm font-medium" data-id="element-906">{label}</div>
        <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-907">
          {format}
        </div>
      </div>
    </div>;
}
function SinglePagePreview({
  label,
  format,
  children
}: {
  label: string;
  format: string;
  children: React.ReactNode;
}) {
  return <div className="space-y-3" data-id="element-908">
      <MiniPage data-id="element-909">{children}</MiniPage>
      <div className="flex items-center justify-between" data-id="element-910">
        <div className="font-space text-sm font-medium" data-id="element-911">{label}</div>
        <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-912">
          {format}
        </div>
      </div>
    </div>;
}
function DocCard({
  number,
  title,
  format,
  type,
  formatIcon,
  description,
  pages,
  howToUse,
  onDownload,
  downloadLabel,
  preview
}: {
  number: string;
  title: string;
  format: string;
  type: 'word' | 'excel';
  formatIcon: React.ReactNode;
  description: string;
  pages: string;
  howToUse: string[];
  onDownload: () => void;
  downloadLabel: string;
  preview: React.ReactNode;
}) {
  const accentColor = type === 'word' ? 'var(--accent-blue)' : 'var(--accent-green)';
  return <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8" data-id="element-913">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-914">
        <div className="lg:col-span-6" data-id="element-915">{preview}</div>
        <div className="lg:col-span-6 flex flex-col" data-id="element-916">
          <div className="flex items-center justify-between mb-3" data-id="element-917">
            <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider" data-id="element-918">
              {number}
            </div>
            <div className="flex items-center gap-2" data-id="element-919">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] tracking-wider" style={{
              background: 'color-mix(in srgb, ' + accentColor + ' 12%, transparent)',
              color: accentColor
            }} data-id="element-920">
                {formatIcon}
                <span data-id="element-921">{format}</span>
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-922">
                {pages}
              </div>
            </div>
          </div>
          <h3 className="font-space text-2xl font-medium tracking-tight mb-3" data-id="element-923">
            {title}
          </h3>
          <p className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed mb-6" data-id="element-924">
            {description}
          </p>

          <div className="mb-6" data-id="element-925">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider mb-3" data-id="element-926">
              HOW TO USE
            </div>
            <ul className="space-y-2" data-id="element-927">
              {howToUse.map((h, i) => <li key={i} className="flex items-start gap-3" data-id="element-928">
                  <div className="font-mono text-[10px] text-[var(--accent-blue)] mt-1 flex-shrink-0" data-id="element-929">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <span className="font-helvetica text-sm leading-relaxed" data-id="element-930">
                    {h}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="mt-auto pt-4 border-t border-[var(--divider)] flex items-center justify-between gap-4" data-id="element-931">
            <div className="font-helvetica text-xs text-[var(--text-muted)]" data-id="element-932">
              Opens in{' '}
              {type === 'word' ? 'Word, Pages, Google Docs' : 'Excel, Numbers, Google Sheets'}
            </div>
            <button onClick={onDownload} className="px-4 py-2 bg-[var(--text-ink)] text-white rounded-full font-helvetica text-sm font-medium hover:bg-[var(--accent-blue)] transition-colors inline-flex items-center gap-2 flex-shrink-0" data-id="element-933">
              <DownloadIcon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-934" />
              {downloadLabel}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  Mini-page contents (the visual previews inside the section)        */
/* ------------------------------------------------------------------ */
// ---- INVOICE preview ----
function InvoicePreviewMain() {
  return <div className="w-full h-full flex flex-col text-[6px] leading-tight bg-white" data-id="element-935">
      {/* Full-bleed blue header */}
      <div className="p-2 text-white" style={{
      background: '#1A2BFF'
    }} data-id="element-936">
        <div className="font-mono text-[3.5px] tracking-widest opacity-75" style={{
        letterSpacing: '0.22em'
      }} data-id="element-937">
          METRO · BARCELONA · FACTURA
        </div>
        <div className="flex items-end justify-between mt-2" data-id="element-938">
          <div data-id="element-939">
            <div className="text-[3.5px] tracking-widest opacity-70" data-id="element-940">
              DOCUMENT
            </div>
            <div className="font-space font-bold text-[18px] leading-[0.88] tracking-tight" data-id="element-941">
              Factura.
            </div>
          </div>
          <div className="text-right" data-id="element-942">
            <div className="text-[3.5px] tracking-widest opacity-70" data-id="element-943">
              N.º · SERIE A
            </div>
            <div className="font-bold text-[9px] mt-0.5" data-id="element-944">
              <span className="bg-white/20 px-1 rounded-sm" data-id="element-945">2026-001</span>
            </div>
          </div>
        </div>
        <div className="border-t border-white/40 mt-1.5 pt-1 text-[3.5px] opacity-85 flex justify-between" data-id="element-946">
          <span data-id="element-947">NIF B12345678 · Barcelona</span>
          <span data-id="element-948">metroagency.co</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-2 flex-1 flex flex-col" data-id="element-949">
        <div className="grid grid-cols-2 gap-2 mb-2" data-id="element-950">
          <div data-id="element-951">
            <div className="text-[3.5px] text-[var(--text-muted)] tracking-widest font-bold" data-id="element-952">
              DESTINATARIO
            </div>
            <div className="text-[5px] font-bold" data-id="element-953">[ Razón social ]</div>
            <div className="text-[4px] text-[var(--text-muted)]" data-id="element-954">
              NIF [ XXXXXXXXX ]
            </div>
          </div>
          <div className="text-right" data-id="element-955">
            <div className="text-[3.5px] text-[var(--text-muted)] tracking-widest font-bold" data-id="element-956">
              PROYECTO
            </div>
            <div className="text-[5px] font-bold" data-id="element-957">[ Proyecto ]</div>
          </div>
        </div>

        <div className="flex items-baseline gap-1 mb-0.5" data-id="element-958">
          <span className="font-mono text-[3.5px] font-bold text-[var(--accent-blue)] tracking-widest" data-id="element-959">
            01 ·
          </span>
          <span className="font-space font-bold text-[6px]" data-id="element-960">
            Detalle de operaciones
          </span>
        </div>
        <div className="bg-[var(--text-ink)] text-white grid grid-cols-12 px-1 py-0.5 text-[4px] font-bold tracking-wider" data-id="element-961">
          <div className="col-span-7" data-id="element-962">CONCEPTO</div>
          <div className="col-span-2 text-right" data-id="element-963">CANT</div>
          <div className="col-span-3 text-right" data-id="element-964">BASE</div>
        </div>
        {[['Estrategia', 'Discovery + workshops', '20', '€1.800'], ['Diseño', 'Sistema visual + identidad', '40', '€3.600'], ['Web', 'Frontend + CMS', '60', '€5.400'], ['Lanzamiento', 'Producción + soporte', '20', '€1.800']].map(([t, d, q, a], i) => <div key={i} className="grid grid-cols-12 px-1 py-0.5 text-[5px] border-b border-[var(--divider)]" data-id="element-965">
            <div className="col-span-7" data-id="element-966">
              <div className="font-bold" data-id="element-967">{t}</div>
              <div className="text-[3.5px] italic text-[var(--text-muted)]" data-id="element-968">
                {d}
              </div>
            </div>
            <div className="col-span-2 text-right text-[var(--text-muted)]" data-id="element-969">
              {q}
            </div>
            <div className="col-span-3 text-right" data-id="element-970">{a}</div>
          </div>)}

        <div className="mt-1 space-y-0.5 text-[4px] text-[var(--text-muted)]" data-id="element-971">
          <div className="flex justify-between" data-id="element-972">
            <span data-id="element-973">Base imponible</span>
            <span data-id="element-974">€12.600</span>
          </div>
          <div className="flex justify-between" data-id="element-975">
            <span data-id="element-976">IVA 21%</span>
            <span data-id="element-977">€2.646</span>
          </div>
        </div>

        {/* Big total bar */}
        <div className="grid grid-cols-12 mt-1.5" data-id="element-978">
          <div className="col-span-7 bg-[var(--text-ink)] text-white p-1" data-id="element-979">
            <div className="text-[3px] tracking-widest opacity-70" data-id="element-980">
              TOTAL A PAGAR
            </div>
            <div className="font-bold text-[10px] leading-none mt-0.5" data-id="element-981">
              €15.246
            </div>
          </div>
          <div className="col-span-5 text-white p-1 text-right" style={{
          background: '#1A2BFF'
        }} data-id="element-982">
            <div className="text-[3px] tracking-widest opacity-80" data-id="element-983">
              FRA · CONCEPTO
            </div>
            <div className="text-[4px] font-bold mt-0.5 leading-tight" data-id="element-984">
              [ Cliente ]
            </div>
          </div>
        </div>

        <div className="mt-auto pt-1 text-[3.5px] text-[var(--text-muted)] leading-snug" data-id="element-985">
          IBAN ES00 0000 0000 · Net 30
        </div>
      </div>
    </div>;
}
function InvoiceLegalMiniPage() {
  return <div className="w-full h-full p-3 text-[5px] leading-snug flex flex-col" data-id="element-986">
      <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-987">
        ANEXO · CONDICIONES
      </div>
      <div className="font-bold text-[8px] leading-none mb-1.5" data-id="element-988">
        Términos y<br data-id="element-989" />
        condiciones.
      </div>
      {['1 · Identificación fiscal', '2 · Plazos y forma de pago', '3 · IVA', '4 · Retención IRPF', '5 · Reclamaciones', '6 · Protección de datos', '7 · Conservación', '8 · Jurisdicción'].map((s, i) => <div key={i} className="mb-0.5" data-id="element-990">
          <div className="font-bold text-[5px]" data-id="element-991">{s}</div>
          <div className="text-[var(--text-muted)] text-[4px]" data-id="element-992">
            Real Decreto 1619/2012 · Ley 37/1992 (IVA) · RGPD...
          </div>
        </div>)}
      <div className="mt-auto pt-1 border-t border-[var(--text-ink)] text-[4px] text-[var(--text-muted)]" data-id="element-993">
        Metro Agency · NIF B12345678
      </div>
    </div>;
}
// ---- BUDGET preview ----
function BudgetPreviewMain() {
  return <div className="w-full h-full flex flex-col text-[6px] leading-tight bg-white" data-id="element-994">
      {/* Full-bleed dark header */}
      <div className="p-2 text-white" style={{
      background: '#0E0E12'
    }} data-id="element-995">
        <div className="font-mono text-[3.5px] tracking-widest" style={{
        letterSpacing: '0.22em',
        color: '#1A2BFF'
      }} data-id="element-996">
          METRO · BARCELONA · PRESUPUESTO
        </div>
        <div className="flex items-end justify-between mt-2" data-id="element-997">
          <div data-id="element-998">
            <div className="text-[3.5px] tracking-widest opacity-70" data-id="element-999">
              DOCUMENT
            </div>
            <div className="font-space font-bold text-[18px] leading-[0.88] tracking-tight" data-id="element-1000">
              Presu&shy;puesto.
            </div>
          </div>
          <div className="text-right" data-id="element-1001">
            <div className="text-[3.5px] tracking-widest opacity-70" data-id="element-1002">
              VERSIÓN
            </div>
            <div className="font-bold text-[9px] mt-0.5" data-id="element-1003">
              <span className="bg-white/12 px-1 rounded-sm" data-id="element-1004">v1.0</span>
            </div>
          </div>
        </div>
        <div className="border-t border-white/40 mt-1.5 pt-1 text-[3.5px] opacity-85 flex justify-between" data-id="element-1005">
          <span data-id="element-1006">NIF B12345678 · Barcelona</span>
          <span data-id="element-1007">metroagency.co</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-2 flex-1 flex flex-col" data-id="element-1008">
        <div className="grid grid-cols-3 gap-1 mb-1.5 text-[5px]" data-id="element-1009">
          <div data-id="element-1010">
            <div className="text-[3.5px] text-[var(--text-muted)] tracking-widest font-bold" data-id="element-1011">
              CLIENTE
            </div>
            <div className="font-bold" data-id="element-1012">[ Cliente ]</div>
          </div>
          <div data-id="element-1013">
            <div className="text-[3.5px] text-[var(--text-muted)] tracking-widest font-bold" data-id="element-1014">
              PROYECTO
            </div>
            <div className="font-bold" data-id="element-1015">[ Project ]</div>
          </div>
          <div className="text-right" data-id="element-1016">
            <div className="text-[3.5px] text-[var(--text-muted)] tracking-widest font-bold" data-id="element-1017">
              LEAD
            </div>
            <div className="font-bold" data-id="element-1018">[ Name ]</div>
          </div>
        </div>

        {/* Connection pull-quote */}
        <div className="text-white p-1 mb-1.5" style={{
        background: '#1A2BFF'
      }} data-id="element-1019">
          <div className="text-[3px] tracking-widest opacity-80" data-id="element-1020">
            THE CONNECTION
          </div>
          <div className="text-[4.5px] font-bold leading-tight" data-id="element-1021">
            [ El insight estratégico... ]
          </div>
        </div>

        <div className="flex items-baseline gap-1 mb-0.5" data-id="element-1022">
          <span className="font-mono text-[3.5px] font-bold text-[var(--accent-blue)] tracking-widest" data-id="element-1023">
            01 ·
          </span>
          <span className="font-space font-bold text-[6px]" data-id="element-1024">
            Alcance por fase
          </span>
        </div>

        <div className="space-y-0.5 flex-1 overflow-hidden" data-id="element-1025">
          {[{
          p: '01 · DISCOVERY',
          d: 'Research + workshops',
          t: '€3.600'
        }, {
          p: '02 · DESIGN',
          d: 'Sistema + componentes',
          t: '€6.000'
        }, {
          p: '03 · BUILD',
          d: 'Frontend + CMS + QA',
          t: '€5.400'
        }, {
          p: '04 · LAUNCH',
          d: 'Soporte + handover',
          t: '€2.200'
        }].map(row => <div key={row.p} className="py-0.5 border-b border-[var(--divider)] text-[5px]" data-id="element-1026">
              <div className="flex justify-between" data-id="element-1027">
                <span className="font-mono text-[4px] text-[var(--accent-blue)] font-bold" data-id="element-1028">
                  {row.p}
                </span>
                <span className="font-bold" data-id="element-1029">{row.t}</span>
              </div>
              <div className="text-[3.5px] italic text-[var(--text-muted)]" data-id="element-1030">
                {row.d}
              </div>
            </div>)}
        </div>

        {/* Big total bar */}
        <div className="grid grid-cols-12 mt-1.5" data-id="element-1031">
          <div className="col-span-7 text-white p-1" style={{
          background: '#1A2BFF'
        }} data-id="element-1032">
            <div className="text-[3px] tracking-widest opacity-85" data-id="element-1033">
              TOTAL INVERSIÓN
            </div>
            <div className="font-bold text-[10px] leading-none mt-0.5" data-id="element-1034">
              €22.893
            </div>
          </div>
          <div className="col-span-5 text-white p-1 text-right" style={{
          background: '#0E0E12'
        }} data-id="element-1035">
            <div className="text-[3px] tracking-widest opacity-60" data-id="element-1036">
              30 · 40 · 30%
            </div>
            <div className="text-[4px] font-bold mt-0.5 leading-tight" data-id="element-1037">
              Firma · Diseño
              <br data-id="element-1038" />· Launch
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function BudgetLegalMiniPage() {
  return <div className="w-full h-full p-3 text-[5px] leading-snug flex flex-col" data-id="element-1039">
      <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1040">
        ANEXO · CONDICIONES GENERALES
      </div>
      <div className="font-bold text-[8px] leading-none mb-1.5" data-id="element-1041">
        Términos del
        <br data-id="element-1042" />
        presupuesto.
      </div>
      {['1 · Validez de la oferta', '2 · Aceptación y formalización', '3 · Calendario de pagos', '4 · Alcance e imprevistos', '5 · Plazos de entrega', '6 · Propiedad intelectual', '7 · Confidencialidad', '8 · Resolución anticipada', '9 · Limitación de responsabilidad', '10 · Datos y jurisdicción'].map((s, i) => <div key={i} className="text-[4.5px] mb-0.5" data-id="element-1043">
          <span className="font-bold" data-id="element-1044">{s}</span>
        </div>)}
      <div className="mt-auto pt-1 border-t border-[var(--text-ink)] text-[4px] text-[var(--text-muted)]" data-id="element-1045">
        Metro Agency · NIF B12345678
      </div>
    </div>;
}
// ---- DOSSIER preview pages ----
function DossierCover() {
  return <div className="w-full h-full flex flex-col p-3 text-white" style={{
    background: '#1A2BFF'
  }} data-id="element-1046">
      <div className="font-mono text-[4px] tracking-widest opacity-70" style={{
      letterSpacing: '0.2em'
    }} data-id="element-1047">
        METRO · BARCELONA
      </div>
      <div className="flex-1 flex flex-col justify-center" data-id="element-1048">
        <div className="text-[4px] tracking-widest opacity-70 mb-1" data-id="element-1049">
          A PROJECT FOR
        </div>
        <div className="text-[6px] font-bold mb-3" data-id="element-1050">
          <span className="bg-white/20 px-1 rounded-sm" data-id="element-1051">[ Client ]</span>
        </div>
        <div className="font-space font-bold text-[20px] leading-[0.92] tracking-tight" data-id="element-1052">
          <span className="bg-white/20 px-1 rounded-sm" data-id="element-1053">[ Project ]</span>
        </div>
      </div>
      <div className="border-t border-white/40 pt-1 flex justify-between text-[4px] opacity-80" data-id="element-1054">
        <span data-id="element-1055">Where ideas connect.</span>
        <span data-id="element-1056">v1.0</span>
      </div>
    </div>;
}
function DossierTOC() {
  return <div className="w-full h-full p-3 flex flex-col" data-id="element-1057">
      <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-1058">
        CONTENTS
      </div>
      <div className="font-space font-bold text-[16px] leading-none mb-3" data-id="element-1059">
        In&shy;side.
      </div>
      {[['01', 'Executive summary', 'p.02'], ['02', 'The opportunity', 'p.03'], ['03', 'Scope', 'p.04'], ['04', 'Timeline', 'p.05'], ['05', 'Team', 'p.06'], ['06', 'Investment', 'p.07'], ['07', 'Terms', 'p.08']].map(([n, t, p], i) => <div key={i} className="flex items-center gap-1.5 py-0.5 border-b border-[var(--divider)] text-[5px]" data-id="element-1060">
          <span className="font-mono text-[var(--accent-blue)] font-bold w-3" data-id="element-1061">
            {n}
          </span>
          <span className="flex-1 font-medium" data-id="element-1062">{t}</span>
          <span className="font-mono text-[var(--text-muted)] text-[4px]" data-id="element-1063">
            {p}
          </span>
        </div>)}
    </div>;
}
function DossierExecSummary() {
  return <div className="w-full h-full p-3 flex flex-col text-[5px] leading-snug" data-id="element-1064">
      <div className="flex items-baseline gap-2 mb-2" data-id="element-1065">
        <span className="font-bold text-[28px] leading-none" style={{
        color: '#1A2BFF'
      }} data-id="element-1066">
          01
        </span>
        <div data-id="element-1067">
          <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1068">
            EXECUTIVE SUMMARY
          </div>
          <div className="font-bold text-[7px] leading-tight" data-id="element-1069">
            What we're building.
          </div>
        </div>
      </div>
      <p className="text-[5px] leading-snug mb-2 text-[var(--text-ink)]" data-id="element-1070">
        [ One paragraph describing what we're building, for whom, and why... ]
      </p>
      <div className="text-white p-1.5" style={{
      background: '#1A2BFF'
    }} data-id="element-1071">
        <div className="text-[4px] tracking-widest opacity-80 mb-0.5" data-id="element-1072">
          THE CONNECTION
        </div>
        <div className="text-[5px] font-medium leading-snug" data-id="element-1073">
          [ One sentence on the strategic insight. ]
        </div>
      </div>
      <div className="mt-2 space-y-0.5" data-id="element-1074">
        {['01 What success looks like', '02 What\u2019s different', '03 What we need'].map((t, i) => <div key={i} className="text-[5px]" data-id="element-1075">
            <span className="font-mono text-[var(--accent-blue)] font-bold mr-1" data-id="element-1076">
              {t.split(' ')[0]}
            </span>
            <span className="font-medium" data-id="element-1077">
              {t.substring(t.indexOf(' ') + 1)}
            </span>
          </div>)}
      </div>
      <div className="mt-auto pt-1 border-t border-[var(--divider)] flex justify-between text-[4px] text-[var(--text-muted)]" data-id="element-1078">
        <span data-id="element-1079">01 · Executive summary</span>
        <span data-id="element-1080">p.02</span>
      </div>
    </div>;
}
function DossierOpportunity() {
  return <div className="w-full h-full p-3 flex flex-col text-[5px] leading-snug" data-id="element-1081">
      <div className="flex items-baseline gap-2 mb-2" data-id="element-1082">
        <span className="font-bold text-[28px] leading-none" style={{
        color: '#1A2BFF'
      }} data-id="element-1083">
          02
        </span>
        <div data-id="element-1084">
          <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1085">
            OPPORTUNITY
          </div>
          <div className="font-bold text-[7px] leading-tight" data-id="element-1086">
            Why now. Why us.
          </div>
        </div>
      </div>
      <div className="font-bold text-[4px] text-[var(--text-muted)] tracking-widest mb-0.5" data-id="element-1087">
        THE INSIGHT
      </div>
      <div className="bg-[var(--text-ink)] text-white p-1.5 mb-2" data-id="element-1088">
        <div className="text-[3.5px] tracking-widest opacity-70 mb-0.5" data-id="element-1089">
          THE INSIGHT
        </div>
        <div className="font-bold text-[8px] leading-tight" data-id="element-1090">
          [ The one observation that makes this project make sense. ]
        </div>
      </div>
      <div className="font-bold text-[4px] text-[var(--text-muted)] tracking-widest mb-0.5" data-id="element-1091">
        THE APPROACH
      </div>
      <p className="text-[5px] leading-snug" data-id="element-1092">
        [ How we're going to act on the insight... ]
      </p>
      <div className="mt-auto pt-1 border-t border-[var(--divider)] flex justify-between text-[4px] text-[var(--text-muted)]" data-id="element-1093">
        <span data-id="element-1094">02 · Opportunity</span>
        <span data-id="element-1095">p.03</span>
      </div>
    </div>;
}
function DossierSignoff() {
  return <div className="w-full h-full p-3 flex flex-col text-white" style={{
    background: '#0E0E12'
  }} data-id="element-1096">
      <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1097">
        SIGN-OFF
      </div>
      <div className="font-space font-bold text-[16px] leading-[0.95] mt-2" data-id="element-1098">
        Let's see
        <br data-id="element-1099" />
        where this
        <br data-id="element-1100" />
        goes.
      </div>
      <div className="mt-auto grid grid-cols-2 gap-2" data-id="element-1101">
        <div data-id="element-1102">
          <div className="text-[4px] opacity-60 tracking-widest mb-2" data-id="element-1103">
            CLIENT
          </div>
          <div className="border-b border-white/40 h-3 mb-1" data-id="element-1104" />
          <div className="text-[4px] opacity-70" data-id="element-1105">[ Name ]</div>
        </div>
        <div data-id="element-1106">
          <div className="text-[4px] opacity-60 tracking-widest mb-2" data-id="element-1107">
            METRO
          </div>
          <div className="border-b border-white/40 h-3 mb-1" data-id="element-1108" />
          <div className="text-[4px] opacity-70" data-id="element-1109">[ Lead ]</div>
        </div>
      </div>
    </div>;
}
// ---- CLIENT GUIDE preview pages ----
function GuideCover() {
  return <div className="w-full h-full p-3 flex flex-col text-white relative" style={{
    background: '#0E0E12'
  }} data-id="element-1110">
      <div className="font-mono text-[4px] tracking-widest" style={{
      color: '#1A2BFF'
    }} data-id="element-1111">
        PROJECT HANDOVER
      </div>
      <div className="flex-1 flex flex-col justify-center" data-id="element-1112">
        <div className="text-[4px] tracking-widest opacity-60 mb-1" data-id="element-1113">
          A GUIDE FOR
        </div>
        <div className="text-[5px] font-bold mb-2" data-id="element-1114">
          <span className="bg-white/12 px-1 rounded-sm" data-id="element-1115">[ Client ]</span>
        </div>
        <div className="font-space font-bold text-[22px] leading-[0.9]" data-id="element-1116">
          Your
          <br data-id="element-1117" />
          project,
          <br data-id="element-1118" />
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-1119">
            now live.
          </span>
        </div>
      </div>
      <div className="border-t border-white/40 pt-1 flex justify-between text-[4px] opacity-80" data-id="element-1120">
        <span data-id="element-1121">For [ Project ]</span>
        <span data-id="element-1122">[ Date ]</span>
      </div>
    </div>;
}
function GuideAssets() {
  return <div className="w-full h-full p-3 flex flex-col text-[5px] leading-snug" data-id="element-1123">
      <div className="flex items-baseline gap-2 mb-2" data-id="element-1124">
        <span className="font-bold text-[28px] leading-none" style={{
        color: '#1A2BFF'
      }} data-id="element-1125">
          01
        </span>
        <div data-id="element-1126">
          <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1127">
            ASSETS
          </div>
          <div className="font-bold text-[7px] leading-tight" data-id="element-1128">
            Where it lives.
          </div>
        </div>
      </div>
      <div className="text-white p-1.5 mb-2" style={{
      background: '#1A2BFF'
    }} data-id="element-1129">
        <div className="text-[3.5px] tracking-widest opacity-80 mb-0.5" data-id="element-1130">
          YOUR LIBRARY
        </div>
        <div className="font-bold text-[7px]" data-id="element-1131">[ Drive URL ]</div>
      </div>
      {[['/logo', 'Master SVG'], ['/fonts', 'Anton + Space Grotesk'], ['/colors', 'Tokens · CSS · JSON'], ['/templates', 'Email · social · docs'], ['/source', 'Figma + native']].map(([f, c], i) => <div key={i} className="flex justify-between py-0.5 border-b border-[var(--divider)]" data-id="element-1132">
          <span className="font-mono font-bold" data-id="element-1133">{f}</span>
          <span className="text-[var(--text-muted)] text-[4px]" data-id="element-1134">{c}</span>
        </div>)}
      <div className="mt-auto pt-1 border-t border-[var(--divider)] flex justify-between text-[4px] text-[var(--text-muted)]" data-id="element-1135">
        <span data-id="element-1136">01 · Assets</span>
        <span data-id="element-1137">p.02</span>
      </div>
    </div>;
}
function GuideVoice() {
  return <div className="w-full h-full p-3 flex flex-col text-[5px] leading-snug" data-id="element-1138">
      <div className="flex items-baseline gap-2 mb-2" data-id="element-1139">
        <span className="font-bold text-[28px] leading-none" style={{
        color: '#1A2BFF'
      }} data-id="element-1140">
          03
        </span>
        <div data-id="element-1141">
          <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1142">
            VOICE
          </div>
          <div className="font-bold text-[7px] leading-tight" data-id="element-1143">
            Writing in brand.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1 mb-1" data-id="element-1144">
        <div className="text-white p-1" style={{
        background: '#1A2BFF'
      }} data-id="element-1145">
          <div className="text-[3.5px] tracking-widest opacity-80" data-id="element-1146">USE</div>
          <div className="text-[4px] mt-0.5" data-id="element-1147">
            connect · build · craft · campaign · real · honest
          </div>
        </div>
        <div className="bg-[var(--text-ink)] text-white p-1" data-id="element-1148">
          <div className="text-[3.5px] tracking-widest opacity-80" data-id="element-1149">AVOID</div>
          <div className="text-[4px] mt-0.5" data-id="element-1150">
            magic · wow · rock · synergy · disrupt · journey
          </div>
        </div>
      </div>
      <div className="space-y-0.5 mt-1" data-id="element-1151">
        <div className="text-[4px] line-through text-[var(--text-muted)]" data-id="element-1152">
          Let's create some magic!
        </div>
        <div className="text-[4.5px] font-bold" data-id="element-1153">
          Let's create something that matters.
        </div>
      </div>
      <div className="mt-auto pt-1 border-t border-[var(--divider)] flex justify-between text-[4px] text-[var(--text-muted)]" data-id="element-1154">
        <span data-id="element-1155">03 · Voice</span>
        <span data-id="element-1156">p.04</span>
      </div>
    </div>;
}
function GuideSupport() {
  return <div className="w-full h-full p-3 flex flex-col text-[5px] leading-snug" data-id="element-1157">
      <div className="flex items-baseline gap-2 mb-2" data-id="element-1158">
        <span className="font-bold text-[28px] leading-none" style={{
        color: '#1A2BFF'
      }} data-id="element-1159">
          04
        </span>
        <div data-id="element-1160">
          <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1161">
            SUPPORT
          </div>
          <div className="font-bold text-[7px] leading-tight" data-id="element-1162">
            When to call.
          </div>
        </div>
      </div>
      <div className="text-white p-1.5 mb-2" style={{
      background: '#1A2BFF'
    }} data-id="element-1163">
        <div className="text-[3.5px] tracking-widest opacity-80 mb-0.5" data-id="element-1164">
          SUPPORT WINDOW
        </div>
        <div className="text-[6px] font-bold leading-tight" data-id="element-1165">
          30 days post-launch.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1 text-[4px]" data-id="element-1166">
        <div data-id="element-1167">
          <div className="font-bold mb-0.5" data-id="element-1168">Call us</div>
          <div className="text-[var(--text-muted)] leading-tight" data-id="element-1169">
            → broken
            <br data-id="element-1170" />
            → new pages
            <br data-id="element-1171" />→ integrations
          </div>
        </div>
        <div data-id="element-1172">
          <div className="font-bold mb-0.5" data-id="element-1173">DIY</div>
          <div className="text-[var(--text-muted)] leading-tight" data-id="element-1174">
            → copy
            <br data-id="element-1175" />
            → blog
            <br data-id="element-1176" />→ team
          </div>
        </div>
      </div>
      <div className="mt-auto pt-1 border-t border-[var(--divider)] flex justify-between text-[4px] text-[var(--text-muted)]" data-id="element-1177">
        <span data-id="element-1178">04 · Support</span>
        <span data-id="element-1179">p.05</span>
      </div>
    </div>;
}
function GuideContact() {
  return <div className="w-full h-full p-3 flex flex-col" style={{
    background: '#EDEEF1'
  }} data-id="element-1180">
      <div className="font-mono text-[4px] text-[var(--accent-blue)] tracking-widest" data-id="element-1181">
        CONTACT
      </div>
      <div className="font-space font-bold text-[14px] leading-[0.95] mt-2" data-id="element-1182">
        The humans
        <br data-id="element-1183" />
        behind it.
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1 text-[5px]" data-id="element-1184">
        <div data-id="element-1185">
          <div className="text-[4px] text-[var(--text-muted)] tracking-widest mb-0.5" data-id="element-1186">
            LEAD
          </div>
          <div className="font-bold" data-id="element-1187">[ Name ]</div>
        </div>
        <div data-id="element-1188">
          <div className="text-[4px] text-[var(--text-muted)] tracking-widest mb-0.5" data-id="element-1189">
            EMAIL
          </div>
          <div className="font-bold" data-id="element-1190">hello@metroagency.co</div>
        </div>
      </div>
      <div className="mt-auto pt-1 border-t border-[var(--text-ink)]" data-id="element-1191">
        <div className="font-space font-bold text-[10px] leading-tight" data-id="element-1192">
          Where ideas connect.
        </div>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */
export function OperationsDocumentsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="operations" ref={ref} className="min-h-screen px-8 py-24" data-id="element-1193">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-1194">
        <div className="mb-16" data-id="element-1195">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-1196">
            09 / OPERATIONS DOCUMENTS
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-1197">
            Documents that work.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 max-w-2xl font-helvetica" data-id="element-1198">
            Four operational templates — invoice, budget, project dossier,
            client guide — built in the brand system and ready to download as
            editable Word or Excel files. Spain-legal-compliant. Multi-page.
            Every placeholder is marked so you can find them at a glance.
          </p>
        </div>

        <div className="space-y-10" data-id="element-1199">
          {/* 09.1 — Invoice */}
          <DocCard number="09.1" title="Invoice / Factura" format=".XLS" type="excel" pages="2 pages" formatIcon={<FileSpreadsheetIcon className="w-3 h-3" data-id="element-1201" />} description="Spain-legal invoice (Real Decreto 1619/2012 + Ley 37/1992 IVA). NIF, fiscal address, fecha de expedición + operación, base imponible, IVA 21%, retención IRPF. Line items each have an editable description field. Page 2 is the full legal annex." howToUse={['Update the invoice number (serie A / 2026-XXX) and dates at the top.', 'Replace destinatario data: razón social, NIF, domicilio fiscal, contacto.', 'Edit each line item — the bold concept plus the italic description underneath. Add or remove rows as needed.', 'For autónomos invoicing companies, fill the retención IRPF row (15% standard, 7% nuevos autónomos).', 'Replace IBAN and bank details before sending. Keep page 2 (legal annex) as-is.']} onDownload={() => downloadOfficeDoc(invoiceHTML, 'Metro_Factura_Template', 'excel')} downloadLabel="Download .xls" preview={<MultiPagePreview label="Invoice · A4 · 2 pages" format="Excel · .xls" pages={[<InvoicePreviewMain key="m" data-id="element-1203" />, <InvoiceLegalMiniPage key="l" data-id="element-1204" />, <div key="end" className="w-full h-full p-3 flex items-center justify-center" style={{
          background: '#EDEEF1'
        }} data-id="element-1205">
                    <div className="text-center" data-id="element-1206">
                      <div className="font-mono text-[5px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1207">
                        END
                      </div>
                      <div className="font-space font-bold text-[10px] leading-tight" data-id="element-1208">
                        2 pages
                        <br data-id="element-1209" />
                        editable .xls
                      </div>
                    </div>
                  </div>]} data-id="element-1202" />} data-id="element-1200" />

          {/* 09.2 — Budget */}
          <DocCard number="09.2" title="Project Budget / Presupuesto" format=".XLS" type="excel" pages="2 pages" formatIcon={<FileSpreadsheetIcon className="w-3 h-3" data-id="element-1211" />} description="Phase-by-phase scope with editable line descriptions, automatic-feel totals with contingency and IVA, 3-stage payment schedule, plus a full annex of 10 condiciones generales de contratación (validez, IP, cancelación, jurisdicción, RGPD)." howToUse={['Replace placeholders for cliente, NIF, proyecto, version and fecha.', 'Each phase has a short blurb plus a description column per line item — edit both to fit the project.', 'Subtotals, contingencia and IVA update if you change the figures. Keep formulas if downloaded into Excel.', 'Send alongside the Project Dossier — this is the financial detail; the dossier is the narrative.', 'Page 2 is the legal annex — leave the structure, only edit if your terms differ.']} onDownload={() => downloadOfficeDoc(budgetHTML, 'Metro_Presupuesto_Template', 'excel')} downloadLabel="Download .xls" preview={<MultiPagePreview label="Budget · A4 · 2 pages" format="Excel · .xls" pages={[<BudgetPreviewMain key="m" data-id="element-1213" />, <BudgetLegalMiniPage key="l" data-id="element-1214" />, <div key="end" className="w-full h-full p-3 flex items-center justify-center" style={{
          background: '#EDEEF1'
        }} data-id="element-1215">
                    <div className="text-center" data-id="element-1216">
                      <div className="font-mono text-[5px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1217">
                        END
                      </div>
                      <div className="font-space font-bold text-[10px] leading-tight" data-id="element-1218">
                        2 pages
                        <br data-id="element-1219" />
                        editable .xls
                      </div>
                    </div>
                  </div>]} data-id="element-1212" />} data-id="element-1210" />

          {/* 09.3 — Project Dossier */}
          <DocCard number="09.3" title="Project Dossier" format=".DOC" type="word" pages="9 pages" formatIcon={<FileTextIcon className="w-3 h-3" data-id="element-1221" />} description="Full proposal document. Full-bleed blue cover, contents page, then seven chapter pages: executive summary, opportunity (with insight pull-quote), scope, timeline, team, investment, terms. Closes on a dark sign-off page with signature blocks." howToUse={['Cover · replace [ Client ] and [ Project ] placeholders. Keep the full-bleed blue.', 'Executive summary · write 4–6 sentences. The blue callout is "the connection we\u2019re making" — one sentence.', 'Opportunity · the dark pull-quote is the strategic insight. Use it.', 'Scope, timeline, team, investment · standard fill-in. The investment page mirrors the budget total.', 'Terms · 9 plain-English clauses, mostly mirrors of the budget legal annex.', 'Sign-off page is the closer. Export to PDF for signing; keep the .doc for the next project.']} onDownload={() => downloadOfficeDoc(dossierHTML, 'Metro_Project_Dossier', 'word')} downloadLabel="Download .doc" preview={<MultiPagePreview label="Project Dossier · A4 · 9 pages" format="Word · .doc" pages={[<DossierCover key="c" data-id="element-1223" />, <DossierTOC key="t" data-id="element-1224" />, <DossierExecSummary key="e" data-id="element-1225" />, <DossierOpportunity key="o" data-id="element-1226" />, <div key="more" className="w-full h-full p-3 flex flex-col" style={{
          background: '#EDEEF1'
        }} data-id="element-1227">
                    <div className="font-mono text-[4px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1228">
                      + 4 MORE PAGES
                    </div>
                    <div className="font-space font-bold text-[7px] leading-tight mb-2" data-id="element-1229">
                      Scope · Timeline ·<br data-id="element-1230" />
                      Team · Investment ·<br data-id="element-1231" />
                      Terms
                    </div>
                    <div className="space-y-0.5 text-[4px]" data-id="element-1232">
                      {['03', '04', '05', '06', '07'].map(n => <div key={n} className="flex justify-between py-0.5 border-b border-[var(--divider)]" data-id="element-1233">
                          <span className="font-mono text-[var(--accent-blue)] font-bold" data-id="element-1234">
                            {n}
                          </span>
                          <span className="font-mono text-[var(--text-muted)]" data-id="element-1235">
                            p.0{n}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto pt-1 border-t border-[var(--divider)] text-[4px] text-[var(--text-muted)]" data-id="element-1236">
                      Plus sign-off →
                    </div>
                  </div>, <DossierSignoff key="s" data-id="element-1237" />]} data-id="element-1222" />} data-id="element-1220" />

          {/* 09.4 — Client Guide */}
          <DocCard number="09.4" title="Client Guide" format=".DOC" type="word" pages="7 pages" formatIcon={<FileTextIcon className="w-3 h-3" data-id="element-1239" />} description="Post-launch handover. Bold full-bleed black cover, welcome / TOC, then five chapter pages: brand assets, website admin, content & voice rules, support window, and contact. Closes on a light grey page with the brand sign-off." howToUse={['Cover · replace [ Client ], [ Project ], [ Date ]. Keep the dark cover for impact.', 'Section 01 · paste real URLs for the asset library. The blue callout is the library link.', 'Section 02 · fill CMS URL, admin email, hosting and domain renewal. Adjust the DIY vs developer columns to match the build.', 'Section 03 · the voice section is already populated with the brand rules. Edit only if the client has specific terminology.', 'Section 04 · response times can be adjusted per support contract.', 'Section 05 · enter the real account lead and contact. Export to PDF for the final handover; keep the .doc for the next project.']} onDownload={() => downloadOfficeDoc(clientGuideHTML, 'Metro_Client_Guide', 'word')} downloadLabel="Download .doc" preview={<MultiPagePreview label="Client Guide · A4 · 7 pages" format="Word · .doc" pages={[<GuideCover key="c" data-id="element-1241" />, <GuideAssets key="a" data-id="element-1242" />, <GuideVoice key="v" data-id="element-1243" />, <GuideSupport key="s" data-id="element-1244" />, <div key="more" className="w-full h-full p-3 flex flex-col" style={{
          background: '#EDEEF1'
        }} data-id="element-1245">
                    <div className="font-mono text-[4px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1246">
                      + 2 MORE PAGES
                    </div>
                    <div className="font-space font-bold text-[7px] leading-tight mb-2" data-id="element-1247">
                      Website admin ·<br data-id="element-1248" />
                      Contact
                    </div>
                    <div className="space-y-0.5 text-[4px]" data-id="element-1249">
                      {['02 Website', '05 Contact'].map(n => <div key={n} className="flex justify-between py-0.5 border-b border-[var(--divider)]" data-id="element-1250">
                          <span className="font-mono text-[var(--accent-blue)] font-bold" data-id="element-1251">
                            {n}
                          </span>
                          <span className="font-mono text-[var(--text-muted)]" data-id="element-1252">
                            →
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto pt-1 border-t border-[var(--divider)] text-[4px] text-[var(--text-muted)]" data-id="element-1253">
                      Closes on contact →
                    </div>
                  </div>, <GuideContact key="ct" data-id="element-1254" />]} data-id="element-1240" />} data-id="element-1238" />
        </div>
      </motion.div>
    </section>;
}